import { t as tCem } from '../utils/i18n'
/**
 * MonitorView — Visualizador de cámaras rediseñado
 *
 * Layout de 3 paneles:
 *   [ Buscador de sitios ] [ Grid de cámaras ] [ Panel derecho: canales / grabaciones / jobs ]
 *
 * Características:
 *   - Búsqueda rápida por código, nombre o dirección (600+ sitios)
 *   - Al seleccionar sitio muestra todos los NVRs y canales disponibles
 *   - Búsqueda de grabaciones: rango de fechas + ventana horaria diaria
 *   - Jobs de analíticas retroactivas con progreso en tiempo real
 */

import { useState, useEffect, useCallback, useRef, useReducer } from 'react'
import { Camera, History, FlaskConical, AlertTriangle, Car, Maximize2, Aperture, Volume2, VolumeX, Mic, Gamepad2 } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import WhepPlayer from '../components/WhepPlayer'
import HlsPlayer, { buildHlsUrl, getSiteM4Host } from '../components/HlsPlayer'
import ForensicGallery from '../components/ForensicGallery'
import {
  captureCellFrame, downloadBlob, makeSnapshotFilename,
  uploadSnapshotToSensorStore,
} from '../lib/snapshot'
import PtzControlPanel from '../components/PtzControlPanel'
import TtsSpeakModal from '../components/TtsSpeakModal'
import NarrativeModal from '../components/NarrativeModal'
import { usePrefetchOnHover } from '../hooks/usePrefetchOnHover'

// LocalStorage key compartido con MapView Q1 — operador conserva su preferencia
// de calidad entre vistas (rtc | main | sub).
const QUALITY_KEY = 'retaliq_map_quality'

// Inyecta keyframes una sola vez por sesión.
if (typeof document !== 'undefined' && !document.getElementById('monitor-styles')) {
  const s = document.createElement('style')
  s.id = 'monitor-styles'
  s.textContent = `
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes cell-pulse {
      0%   { box-shadow: 0 0 0 3px rgba(239,68,68,0.65), 0 0 14px rgba(239,68,68,0.9); }
      60%  { box-shadow: 0 0 0 6px rgba(239,68,68,0.0),  0 0 24px rgba(239,68,68,0.3); }
      100% { box-shadow: 0 0 0 3px rgba(239,68,68,0.65), 0 0 14px rgba(239,68,68,0.9); }
    }
    /* Acciones flotantes (snapshot, fullscreen) aparecen al pasar el mouse */
    [data-cell-root]:hover [data-cell-actions] { opacity: 1; }
    /* En touch devices siempre visibles */
    @media (hover: none) { [data-cell-actions] { opacity: 1 !important; } }
  `
  document.head.appendChild(s)
}

// ─── Constantes ──────────────────────────────────────────────────────────────

const LAYOUTS = [
  { label: '1×1', cols: 1, count: 1  },
  { label: '2×2', cols: 2, count: 4  },
  { label: '3×3', cols: 3, count: 9  },
  { label: '4×4', cols: 4, count: 16 },
]

const ANALYTIC_OPTS = [
  { key: 'lpr',      label: 'LPR',       desc: 'Reconocimiento de placas' },
  { key: 'faces',    label: 'Rostros',   desc: 'Identificación facial'     },
  { key: 'persons',  label: 'Personas',  desc: 'Detección de personas'    },
  { key: 'vehicles', label: 'Vehículos', desc: 'Detección de vehículos'   },
]

const STATUS_COLOR = {
  pending   : '#6b7280',
  running   : '#3b82f6',
  completed : '#10b981',
  failed    : '#ef4444',
  cancelled : '#9ca3af',
}

const STATUS_LABEL = {
  pending   : 'Pendiente',
  running   : 'Procesando',
  completed : 'Completado',
  failed    : 'Error',
  cancelled : 'Cancelado',
}

// ─── Estilos ─────────────────────────────────────────────────────────────────

const S = {
  root: {
    display: 'flex', height: '100%', overflow: 'hidden',
    background: 'var(--bg-root)',
  },

  // Panel izquierdo — buscador de sitios
  sitePanel: {
    width: 260, display: 'flex', flexDirection: 'column',
    borderRight: '1px solid var(--border)', background: 'var(--bg-surface)',
    flexShrink: 0,
  },
  sitePanelHeader: {
    padding: '10px 12px 6px', borderBottom: '1px solid var(--border)', flexShrink: 0,
  },
  searchInput: {
    width: '100%', padding: '7px 10px', borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--bg-root)',
    color: 'var(--text-primary)', fontSize: 12, outline: 'none',
    boxSizing: 'border-box',
  },
  siteList: { flex: 1, overflowY: 'auto', padding: '4px 6px' },
  siteRow: (active) => ({
    padding: '8px 10px', borderRadius: 6, marginBottom: 2, cursor: 'pointer',
    background: active ? 'var(--bg-active)' : 'transparent',
    borderLeft: active ? '3px solid var(--accent)' : '3px solid transparent',
    transition: 'background 0.1s',
  }),
  siteName: { fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 1 },
  siteMeta: { fontSize: 10, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'center' },
  siteCode: {
    fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
    background: 'var(--bg-muted)', color: 'var(--text-muted)', letterSpacing: '0.5px',
  },

  // Centro — grid de cámaras
  center: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)',
    flexShrink: 0, flexWrap: 'wrap',
  },
  // Cuadros estandarizados aspect 16:9 forzado, centrados en el wrap.
  // El grid se calcula para que TODAS las celdas tengan exactamente el mismo
  // tamaño sin importar si una está vacía o si hay 4, 9 o 16. El wrap es
  // overflow:auto para que en pantallas chicas pueda hacer scroll en vez de
  // distorsionar.
  gridWrap: {
    flex: 1, padding: 8, overflow: 'auto',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  grid: (cols) => ({
    width: '100%', maxWidth: '100%',
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gridAutoRows: '1fr',  // todas las filas iguales
    gap: 5,
    // Aspect ratio del grid completo: cols columnas × N filas con cada celda 16:9.
    aspectRatio: `${cols * 16} / ${cols * 9}`,
  }),
  cell: (sel) => ({
    background: '#050505', borderRadius: 6, overflow: 'hidden', position: 'relative',
    border: sel ? '2px solid var(--accent)' : '2px solid #1a1a1a',
    cursor: 'pointer',
    // 16:9 forzado por celda. La imagen MJPEG cubre con objectFit:cover (corta
    // bordes si no calza). NUNCA deja banda negra ni se deforma.
    aspectRatio: '16 / 9',
    minHeight: 0,
  }),
  cellImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  cellLabel: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
    color: '#fff', fontSize: 10, fontWeight: 500, padding: '12px 7px 4px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
  },
  badge: (color) => ({
    fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
    background: color, color: '#fff',
  }),

  // Acciones flotantes en la celda. Visibles SIEMPRE con opacidad alta —
  // antes estaban en hover-only + bg/borde semi-transparente lo que las hacía
  // casi invisibles sobre imágenes claras. Ahora: fondo negro sólido + borde
  // blanco visible + sombra; en hover sube a opacity completa.
  cellActions: {
    position: 'absolute', top: 6, right: 6, zIndex: 5,
    display: 'flex', gap: 5, opacity: 0.85,
    transition: 'opacity 0.15s',
    pointerEvents: 'none',
  },
  cellActionBtn: {
    width: 30, height: 30, borderRadius: 6,
    // Fondo casi-negro opaco + borde blanco bien visible + sombra exterior:
    // garantiza alto contraste sobre cualquier imagen (clara, oscura, mixta).
    // Paulo es daltónico — la diferenciación viene de luminosidad + borde, no tono.
    background: 'rgba(15,15,15,0.92)',
    color: '#fff',
    border: '1.5px solid rgba(255,255,255,0.55)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', pointerEvents: 'auto',
  },
  cellToast: {
    position: 'absolute', top: 38, right: 4, zIndex: 6,
    background: 'rgba(0,0,0,0.85)', color: '#fff',
    padding: '5px 9px', borderRadius: 6, fontSize: 10,
    pointerEvents: 'none',
  },

  // Panel derecho
  rightPanel: {
    width: 300, borderLeft: '1px solid var(--border)',
    background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column',
    flexShrink: 0,
  },
  tabs: { display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  tab: (active) => ({
    flex: 1, padding: '9px 4px', fontSize: 11, fontWeight: 600,
    cursor: 'pointer', border: 'none', background: 'transparent',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
  }),
  panelBody: { flex: 1, overflowY: 'auto', padding: 12 },

  // Form elements
  label: { fontSize: 11, color: 'var(--text-muted)', marginBottom: 3, display: 'block' },
  input: {
    width: '100%', padding: '6px 8px', borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--bg-root)',
    color: 'var(--text-primary)', fontSize: 12, outline: 'none', boxSizing: 'border-box',
  },
  row2: { display: 'flex', gap: 6, marginBottom: 10 },
  btnPrimary: {
    padding: '7px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, background: 'var(--accent)', color: '#fff',
    width: '100%',
  },
  btnSecondary: {
    padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
    cursor: 'pointer', fontSize: 11, fontWeight: 500,
    background: 'var(--bg-root)', color: 'var(--text-primary)',
  },
  section: { marginBottom: 14 },
  sectionTitle: {
    fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6,
  },

  // Recording result row
  recRow: (active) => ({
    padding: '7px 9px', borderRadius: 6, marginBottom: 4,
    background: active ? 'var(--bg-active)' : 'var(--bg-root)',
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    cursor: 'pointer',
  }),

  // Analytics job row
  jobRow: {
    padding: '8px 10px', borderRadius: 6, marginBottom: 6,
    background: 'var(--bg-root)', border: '1px solid var(--border)',
  },

  // Empty state
  empty: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    color: 'var(--text-muted)', gap: 10,
  },
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDt(iso) {
  if (!iso) return ''
  return iso.replace('T', ' ').slice(0, 19)
}

function fmtDur(start, end) {
  try {
    const s = new Date(start.includes('T') ? start : start.replace(' ', 'T'))
    const e = new Date(end.includes('T') ? end : end.replace(' ', 'T'))
    const sec = Math.round((e - s) / 1000)
    if (sec < 0) return ''
    const m = Math.floor(sec / 60), s2 = sec % 60
    return `${m}:${String(s2).padStart(2, '0')}`
  } catch { return '' }
}

// ─── CameraCell ──────────────────────────────────────────────────────────────

function CameraCell({
  nvr, channel, label, selected, onSelect, playback, token,
  singleCell = false, pulsing = false,
  quality = 'auto',   // 'rtc' | 'main' | 'sub' | 'auto'
  siteId = null,      // requerido para RTC (grant endpoint)
  siteName = '',      // nombre humano del sitio (para watermark snapshot)
  operatorName = '',  // email del operador (para watermark snapshot)
  onQualityChange = null, // si está seteado, badge calidad es clickeable
}) {
  // Resolve siteId — si el parent no lo pasa (activeSite null en grid mix),
  // lo derivamos del nvr.id usando mapping local de NVR → site. Tabla cerrada
  // por ahora (3 sites productivos); cuando crezca, mover a un fetch /sites/by-nvr.
  // IMPORTANTE: declarado AL INICIO del componente porque varios useCallback
  // de abajo lo capturan en sus deps. Si lo dejamos abajo, JS lanza TDZ
  // ("Cannot access 'effSiteId' before initialization") cuando React procesa
  // los useCallback de arriba a abajo y necesita resolver la dep array.
  const NVR_TO_SITE_INIT = { 4: 17, 5: 18, 6: 19, 22: 20 }   // Latadia, Cat System (legacy), Hites Alameda, Cat System (M4)
  const effSiteIdInit = siteId ?? (nvr?.id != null ? NVR_TO_SITE_INIT[nvr.id] : null) ?? null
  const effSiteId = effSiteIdInit

  const [qualityMenuOpen, setQualityMenuOpen] = useState(false)
  const [ptzOpen, setPtzOpen] = useState(false)
  const [ttsOpen, setTtsOpen] = useState(false)
  const qualityMenuWrapRef = useRef(null) // wrapper trigger+popover para click-outside
  // PTZ: lee credenciales ONVIF guardadas por cámara en localStorage.
  // Formato: retaliq_ptz_cam_<nvrId>_<channel> → JSON {host,port,username,password,profile_token}
  const ptzKey = nvr?.id != null && channel != null ? `retaliq_ptz_cam_${nvr.id}_${channel}` : null
  const ptzConfig = (() => {
    if (!ptzKey) return null
    try { return JSON.parse(localStorage.getItem(ptzKey) || 'null') } catch { return null }
  })()
  const ptzAvailable = !!ptzConfig?.host && !!ptzConfig?.profile_token
  const cellRootRef = useRef(null) // raíz para snapshot + fullscreen
  const imgRef     = useRef(null)
  const retryRef   = useRef(null)   // timeout id activo
  const attemptRef = useRef(0)      // intentos consecutivos sin frame
  const [retrying, setRetrying] = useState(false)
  const [toast, setToast] = useState(null) // mini mensaje sobre la cell

  // Cerrar menu de calidad al clickear afuera o presionar ESC.
  // Listener solo activo cuando el menu está abierto para no impactar perf.
  useEffect(() => {
    if (!qualityMenuOpen) return
    const onDocPointerDown = (e) => {
      if (qualityMenuWrapRef.current && !qualityMenuWrapRef.current.contains(e.target)) {
        setQualityMenuOpen(false)
      }
    }
    const onKey = (e) => { if (e.key === 'Escape') setQualityMenuOpen(false) }
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [qualityMenuOpen])

  // Toast efímero (1.5s) para feedback inmediato del snapshot/fullscreen.
  const flashToast = useCallback((text) => {
    setToast(text)
    const t = setTimeout(() => setToast(null), 1500)
    return () => clearTimeout(t)
  }, [])

  const handleSnapshot = useCallback(async (e) => {
    e?.stopPropagation()
    if (!cellRootRef.current) return
    try {
      const blob = await captureCellFrame(cellRootRef.current, {
        siteName: siteName || `Sitio ${siteId ?? ''}`,
        cameraName: label || `Canal ${channel}`,
        operatorName,
      })
      const filename = makeSnapshotFilename({
        siteName: siteName || `site${siteId ?? ''}`,
        cameraName: label || `ch${channel}`,
      })
      downloadBlob(blob, filename)
      // Upload al SensorStore deshabilitado hasta que exista /api/sensors/manual_snapshot
      // (modelo ManualSnapshot en backend). Por ahora solo descarga local.
      // uploadSnapshotToSensorStore(blob, { siteId, channel, siteName, cameraName: label, operatorName }, token)
      flashToast('Snapshot descargado')
    } catch (err) {
      console.error('[snapshot]', err)
      flashToast(err.message?.slice(0, 60) || 'Error snapshot')
    }
  }, [siteId, channel, label, siteName, operatorName, token, flashToast])

  const handleFullscreen = useCallback((e) => {
    e?.stopPropagation()
    const node = cellRootRef.current
    if (!node) return
    if (document.fullscreenElement === node) {
      document.exitFullscreen?.().catch(()=>{})
    } else {
      (node.requestFullscreen?.() || Promise.resolve()).catch(err => {
        flashToast('Sin permiso fullscreen')
      })
    }
  }, [flashToast])

  // Audio recepción — toggle mute/unmute del <video> dentro de la cell.
  // Por defecto el WhepPlayer arranca muted (autoplay policy de Chrome). Esta
  // función lo permite activar manualmente. Si MediaMTX path no incluye audio
  // (caso típico Dahua/Hikvision sub-stream), avisamos al operador.
  const [muted, setMuted] = useState(true)
  const handleToggleAudio = useCallback((e) => {
    e?.stopPropagation()
    const v = cellRootRef.current?.querySelector('video')
    if (!v) return flashToast('Video no listo')
    // Chequear si hay audio en el stream — webkitAudioDecodedByteCount o videoTracks
    const hasAudio = (v.audioTracks?.length > 0) ||
                     (v.webkitAudioDecodedByteCount && v.webkitAudioDecodedByteCount > 0) ||
                     (v.mozHasAudio === true)
    const next = !muted
    v.muted = next
    setMuted(next)
    if (!next && !hasAudio) {
      // Igual desmuteamos para que cuando empiece a llegar audio se escuche.
      flashToast('Sin audio en este canal')
    } else {
      flashToast(next ? 'Audio silenciado' : 'Audio activado')
    }
  }, [muted, flashToast])

  // TTS hablar — abre modal para enviar texto al speaker SIP del sitio.
  // Bridge cloud→edge aún no implementado, así que por ahora deshabilitado con
  // mensaje claro. UI lista para cuando exista /api/sites/{id}/tts_speak.
  const handleTtsSpeak = useCallback((e) => {
    e?.stopPropagation()
    if (!effSiteId) {
      flashToast('Sin sitio resuelto')
      return
    }
    setTtsOpen(true)
  }, [effSiteId, flashToast])

  // En modo Playback siempre usamos MJPEG (RTC/HLS solo aplican a Vivo).
  // En modo Vivo:
  //  - quality === 'rtc' && hay siteId → WhepPlayer (sub-segundo, fallando hoy)
  //  - quality === 'main' && hay siteId + edge_host configurado → HlsPlayer
  //    directo al M4 vía Tailscale (HD 1920×1080, 4-6s lag, ZERO carga cloud)
  //  - cualquier otro caso → MJPEG por cloud (fallback)
  // useRtc: WebRTC vía WhepPlayer. El sidecar M4 usa siteId+channel del path,
  // NO necesita el objeto `nvr` del frontend (eso es solo para MJPEG cloud que
  // construye URLs con nvr.id). Pre-2026-06-04 requeríamos nvr != null y eso
  // rompía el RTC en Hites Alameda donde el prop nvr no llegaba al ImgCell.
  // effSiteId ya está declarado al inicio del componente (ver comentario arriba).

  const useRtc = quality === 'rtc' && !playback && effSiteId != null && channel != null
  const hlsUrl = (quality === 'main' && !playback && effSiteId != null && nvr != null)
    ? buildHlsUrl(effSiteId, nvr.id, channel)
    : null
  const useHls = !!hlsUrl

  const streamUrl = useCallback((bust = false) => {
    if (!nvr) return ''
    const auth = `?token=${encodeURIComponent(token)}`
    const cb   = bust ? `&_=${Date.now()}` : ''
    if (playback) {
      const { start, end } = playback
      return `/api/nvr/${nvr.id}/playback/${channel}${auth}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&fps=15&width=1280${cb}`
    }
    // 'main' explícito → HD. 'sub' explícito → SD.
    // 'auto' → main si singleCell, sub si grid (preserva comportamiento previo).
    let useMain
    if (quality === 'main')      useMain = true
    else if (quality === 'sub')  useMain = false
    else                          useMain = singleCell
    if (useMain) {
      return `/api/nvr/${nvr.id}/live/${channel}${auth}&fps=25&width=1920&stream=main${cb}`
    }
    return `/api/nvr/${nvr.id}/live/${channel}${auth}&fps=12&width=640&stream=sub${cb}`
  }, [nvr, channel, playback, token, singleCell, quality])

  const clearRetry = useCallback(() => {
    if (retryRef.current) {
      clearTimeout(retryRef.current)
      retryRef.current = null
    }
  }, [])

  const openStream = useCallback((bust = false) => {
    if (!imgRef.current) return
    setRetrying(false)
    imgRef.current.src = streamUrl(bust)
  }, [streamUrl])

  const scheduleRetry = useCallback(() => {
    clearRetry()
    attemptRef.current += 1
    // backoff exponencial acotado a 8s — NVR puede tardar 2-3s en reabrir
    const delay = Math.min(8000, 1000 * 2 ** (attemptRef.current - 1))
    setRetrying(true)
    retryRef.current = setTimeout(() => openStream(true), delay)
  }, [clearRetry, openStream])

  // El useEffect depende SOLO de los inputs primitivos que afectan la URL.
  // Antes dependía de los callbacks (streamUrl, openStream, clearRetry) que
  // se re-creaban en cada render del parent → reseteaba el stream MJPEG
  // cada vez que LiveMonitor re-renderizaba. Con esto el stream queda estable
  // y solo se reabre cuando realmente cambia algo de la URL.
  //
  // Incluímos `useRtc` en deps porque cuando alternamos RTC ↔ MJPEG el
  // imgRef se monta/desmonta (la rama del JSX cambia). Sin esto, al volver
  // de RTC a MJPEG el <img> queda con src vacío y se ve roto.
  useEffect(() => {
    if (useRtc || useHls) return   // RTC/HLS manejan su propio ciclo
    clearRetry()
    attemptRef.current = 0
    setRetrying(false)
    if (imgRef.current) {
      imgRef.current.src = ''
      const t = setTimeout(() => openStream(false), 60)
      return () => { clearTimeout(t); clearRetry() }
    }
    return () => clearRetry()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nvr?.id, channel, playback?.start, playback?.end, singleCell, token, useRtc, useHls, quality])

  function onLoadFrame() {
    if (attemptRef.current !== 0) attemptRef.current = 0
    if (retrying) setRetrying(false)
  }

  return (
    <div
      ref={cellRootRef}
      data-cell-root
      style={{
        ...S.cell(selected),
        // Cuando llega un evento por WS de esta cámara, pulsamos el borde rojo
        // durante ~2s. El glow es visible aunque el cuadro esté en negro.
        ...(pulsing ? {
          borderColor: '#ef4444',
          boxShadow: '0 0 0 3px rgba(239,68,68,0.5), 0 0 14px rgba(239,68,68,0.8)',
          animation: 'cell-pulse 1.2s ease-out infinite',
        } : {}),
        transition: 'box-shadow 0.2s, border-color 0.2s',
      }}
      onClick={() => onSelect(channel)}
    >
      {/* Acciones flotantes — visibles on-hover y siempre en touch */}
      <div data-cell-actions style={S.cellActions}>
        {onQualityChange && !playback && (
          <div ref={qualityMenuWrapRef} style={{ position: 'relative' }}>
            <button
              style={{ ...S.cellActionBtn, width: 'auto', padding: '0 8px', fontSize: 10, fontWeight: 700 }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setQualityMenuOpen(v => !v) }}
              title="Calidad de esta cámara"
              aria-label="Cambiar calidad"
              aria-expanded={qualityMenuOpen}
            >
              {quality === 'rtc' ? 'RTC' : quality === 'main' ? 'HD' : quality === 'sub' ? 'SD' : 'AUTO'} ▾
            </button>
            {qualityMenuOpen && (
              <div
                style={{
                  position: 'absolute', top: 34, right: 0, zIndex: 12,
                  background: 'rgba(15,15,15,0.97)',
                  border: '1.5px solid rgba(255,255,255,0.4)',
                  borderRadius: 7, padding: 4, minWidth: 180,
                  boxShadow: '0 6px 20px rgba(0,0,0,0.7)',
                  pointerEvents: 'auto',
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                {[
                  { v: 'rtc',  label: 'RTC',  hint: 'WebRTC sub-segundo' },
                  { v: 'main', label: 'HD',   hint: 'Stream principal 1080p (~4-6s lag)' },
                  { v: 'sub',  label: 'SD',   hint: 'Sub-stream 640p (MJPEG cloud)' },
                ].map(opt => {
                  const active = quality === opt.v
                  return (
                    <button
                      key={opt.v}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        onQualityChange(opt.v)
                        setQualityMenuOpen(false)
                      }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '8px 12px', fontSize: 12, color: '#fff',
                        fontWeight: active ? 700 : 500,
                        background: active ? 'rgba(37,99,235,0.55)' : 'transparent',
                        border: 'none', cursor: 'pointer', borderRadius: 5,
                        marginBottom: 2,
                      }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.08)' }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
                      aria-pressed={active}
                    >
                      <div style={{ fontWeight: 700, fontSize: 12 }}>
                        {active ? '● ' : '○ '}{opt.label}
                      </div>
                      <div style={{ fontSize: 10, color: '#bbb', marginTop: 1, fontWeight: 400 }}>
                        {opt.hint}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
        <button
          style={S.cellActionBtn}
          onClick={handleSnapshot}
          title="Snapshot (descargar PNG con watermark)"
          aria-label="Tomar snapshot"
        >
          <Aperture size={14} />
        </button>
        {!playback && (
          <>
            <button
              style={S.cellActionBtn}
              onClick={handleToggleAudio}
              title={muted ? 'Activar audio (si el canal lo provee)' : 'Silenciar audio'}
              aria-label={muted ? 'Audio silenciado' : 'Audio activo'}
              aria-pressed={!muted}
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <button
              style={S.cellActionBtn}
              onClick={handleTtsSpeak}
              title="Hablar por el speaker del sitio (disuasión activa) — en desarrollo"
              aria-label="Disuasión activa"
            >
              <Mic size={14} />
            </button>
            {ptzAvailable && (
              <button
                style={S.cellActionBtn}
                onClick={(e) => { e.stopPropagation(); setPtzOpen(v => !v) }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Control PTZ (Pan/Tilt/Zoom)"
                aria-label="Control PTZ"
                aria-pressed={ptzOpen}
              >
                <Gamepad2 size={14} />
              </button>
            )}
          </>
        )}
        <button
          style={S.cellActionBtn}
          onClick={handleFullscreen}
          title="Pantalla completa"
          aria-label="Pantalla completa"
        >
          <Maximize2 size={14} />
        </button>
      </div>
      {toast && <div style={S.cellToast}>{toast}</div>}
      {ptzOpen && ptzAvailable && (
        <PtzControlPanel
          cameraConfig={ptzConfig}
          token={token}
          onClose={() => setPtzOpen(false)}
        />
      )}
      {ttsOpen && effSiteId && (
        <TtsSpeakModal
          siteId={effSiteId}
          channel={channel}
          channelName={label}
          token={token}
          onClose={() => setTtsOpen(false)}
        />
      )}
      {useRtc ? (
        <WhepPlayer
          siteId={effSiteId}
          channel={channel}
          channelName={label}
          token={token}
          compact
          posterUrl={nvr?.id ? `/api/nvr/${nvr.id}/snapshot/${channel}` : null}
        />
      ) : useHls ? (
        <HlsPlayer
          url={hlsUrl}
          channelName={label}
          posterUrl={nvr?.id ? `/api/nvr/${nvr.id}/snapshot/${channel}` : null}
        />
      ) : (typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net') ? (
        // Cement India demo — video MP4 loop en lugar del MJPEG cloud.
        <>
          <video
            ref={el => { if (el) { el.muted = true; el.defaultMuted = true; el.play?.().catch(()=>{}); } }}
            src={`/branding/cement-videos/cement-${(((channel || 0) - 1) % 4 + 4) % 4 + 1}.mp4`}
            autoPlay muted loop playsInline preload="auto"
            style={{ ...S.cellImg, objectFit: 'cover' }}
          />
          <div style={S.cellLabel}>
            <span style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{label}</span>
            <span style={S.badge('#10b981')}>● LIVE</span>
          </div>
        </>
      ) : (
        <>
          <img
            ref={imgRef}
            alt={label}
            style={S.cellImg}
            onLoad={onLoadFrame}
            onError={scheduleRetry}
          />
          {retrying && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.55)', pointerEvents: 'none',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 6,
              color: '#e5e5e5',
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.25)',
                borderTopColor: '#fff',
                animation: 'spin 0.8s linear infinite',
              }} />
              <span style={{ fontSize: 10, letterSpacing: '0.04em' }}>{tCem('Reconectando…','Reconnecting…')}</span>
            </div>
          )}
          <div style={S.cellLabel}>
            <span style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{label}</span>
            <span style={S.badge(playback ? '#f59e0b' : '#10b981')}>
              {playback ? 'REC' : '● VIVO'}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

// ─── SitePanel ────────────────────────────────────────────────────────────────

// Helper: dot de estado, compartido entre SitePanel y CameraNode
function StatusDot({ status, size = 7 }) {
  const colors = { alarm: '#ef4444', warning: '#f59e0b', offline: '#6b7280', ok: '#10b981', online: '#10b981' }
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%',
      background: colors[status] || '#6b7280', display: 'inline-block', flexShrink: 0,
    }} />
  )
}

// ─── CameraNode: row de un canal dentro del tree, draggable + click ──────────

function CameraNode({ nvr, channel, name, online, onPickToCell, onDragStart, token }) {
  // Sprint Performance Live — Día 4: prefetch WHEP en hover.
  // Si el operador deja el mouse ≥200ms encima, dispara handshake en background.
  // Cuando hace click después, switch a la celda es <100ms (pool hit).
  const NVR_TO_SITE_HOVER = { 4: 17, 5: 18, 6: 19, 22: 20 }
  const siteIdForPrefetch = NVR_TO_SITE_HOVER[nvr?.id] ?? null
  const hoverPrefetch = usePrefetchOnHover({
    siteId: siteIdForPrefetch,
    channel,
    token,
    enabled: online && siteIdForPrefetch != null,
  })
  return (
    <div
      draggable
      onDragStart={(e) => {
        const payload = JSON.stringify({ nvrId: nvr.id, channel, name, online })
        e.dataTransfer.setData('application/x-retaliq-camera', payload)
        e.dataTransfer.setData('text/plain', `${nvr.name} · ${name}`)
        e.dataTransfer.effectAllowed = 'copyMove'
        onDragStart?.()
      }}
      onClick={() => onPickToCell?.({ nvrId: nvr.id, channel, name, online })}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 6px 4px 24px',
        fontSize: 11, cursor: 'grab', userSelect: 'none',
        borderRadius: 4, marginBottom: 1,
        opacity: online ? 1 : 0.55,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'var(--bg-hover)'
        hoverPrefetch.onMouseEnter()
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent'
        hoverPrefetch.onMouseLeave()
      }}
      title={`Arrastrar a una celda del grid, o click para agregar a la próxima celda libre`}
    >
      <StatusDot status={online ? 'ok' : 'offline'} size={6} />
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </span>
      <span style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 700 }}>
        CH{String(channel).padStart(2, '0')}
      </span>
    </div>
  )
}

// ─── NvrBranch: sub-tree del NVR con sus canales (lazy load) ─────────────────

function NvrBranch({ nvr, token, onPickToCell }) {
  const [channels, setChannels] = useState(null)  // null = no cargado, [] = cargado vacío
  const [loading, setLoading]   = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetch(`/api/nvr/${nvr.id}/channels`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (alive) { setChannels(d); setLoading(false) } })
      .catch(() => { if (alive) { setChannels([]); setLoading(false) } })
    return () => { alive = false }
  }, [nvr.id, token])

  return (
    <div style={{ marginTop: 2 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '3px 6px 3px 12px',
        fontSize: 10, color: 'var(--text-muted)', fontWeight: 600,
      }}>
        <span style={{ fontSize: 11 }}>📹</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {nvr.name}
        </span>
        <span style={{
          fontSize: 9, padding: '0 5px', borderRadius: 6,
          background: 'var(--bg-muted)', color: 'var(--text-muted)',
        }}>{(nvr.brand || '').toUpperCase()}</span>
      </div>
      {loading && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '3px 6px 3px 24px' }}>{tCem('Cargando…','Loading…')}</div>
      )}
      {!loading && channels && channels.length === 0 && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '3px 6px 3px 24px' }}>Sin canales</div>
      )}
      {!loading && channels && channels.map(c => (
        <CameraNode
          key={`${nvr.id}-${c.channel}`}
          nvr={nvr}
          channel={c.channel}
          name={c.name || `CH${c.channel}`}
          online={c.online !== false}
          onPickToCell={onPickToCell}
          token={token}
        />
      ))}
    </div>
  )
}

// ─── SitePanel: tree expandible, sitio → NVR → cámaras ───────────────────────

function SitePanel({ activeSite, onSelect, token, onPickToCell }) {
  const [query, setQuery]     = useState('')
  const [sites, setSites]     = useState([])
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded]   = useState({})   // { [siteId]: true }
  const [siteNvrs, setSiteNvrs]   = useState({})   // { [siteId]: [nvr] }
  const [allNvrs, setAllNvrs]     = useState(null) // cache de /api/nvr/configs

  useEffect(() => {
    setLoading(true)
    fetch('/api/sites/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setSites(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [token])

  // Lazy: cuando expandimos un sitio por primera vez, cargar sus NVRs.
  const ensureNvrs = useCallback(async (siteId) => {
    if (siteNvrs[siteId]) return
    let list = allNvrs
    if (!list) {
      const r = await fetch('/api/nvr/configs', { headers: { Authorization: `Bearer ${token}` } })
      list = r.ok ? await r.json() : []
      setAllNvrs(list)
    }
    setSiteNvrs(prev => ({ ...prev, [siteId]: list.filter(n => n.site_id === siteId) }))
  }, [siteNvrs, allNvrs, token])

  const toggleExpand = (site, e) => {
    e.stopPropagation()
    const next = !expanded[site.id]
    setExpanded(prev => ({ ...prev, [site.id]: next }))
    if (next) ensureNvrs(site.id)
  }

  const handleRowClick = (site) => {
    onSelect(site)
    if (!expanded[site.id]) {
      setExpanded(prev => ({ ...prev, [site.id]: true }))
      ensureNvrs(site.id)
    }
  }

  const q = query.toLowerCase()
  const filtered = sites.filter(s =>
    !q ||
    s.name?.toLowerCase().includes(q) ||
    s.external_ref?.toLowerCase().includes(q) ||
    s.address?.toLowerCase().includes(q) ||
    s.zone_name?.toLowerCase().includes(q)
  )

  return (
    <div style={S.sitePanel}>
      <div style={S.sitePanelHeader}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: '0.5px' }}>
          SITIOS {sites.length > 0 && <span style={{ color: 'var(--accent)' }}>({sites.length})</span>}
        </div>
        <input
          style={S.searchInput}
          placeholder="Código, nombre, dirección…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div style={S.siteList}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 11 }}>
            Cargando sitios…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 11 }}>
            {query ? 'Sin resultados' : 'No hay sitios'}
          </div>
        )}
        {filtered.map(s => {
          const isExpanded = !!expanded[s.id]
          const nvrs = siteNvrs[s.id]
          return (
            <div key={s.id}>
              <div style={S.siteRow(activeSite?.id === s.id)} onClick={() => handleRowClick(s)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span
                    onClick={(e) => toggleExpand(s, e)}
                    style={{
                      cursor: 'pointer', width: 14, textAlign: 'center',
                      fontSize: 10, color: 'var(--text-muted)',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s', userSelect: 'none',
                    }}
                    title={isExpanded ? 'Colapsar' : 'Expandir cámaras'}
                  >▶</span>
                  <StatusDot status={s.status} />
                  <span style={S.siteName}>{s.name}</span>
                </div>
                <div style={{ ...S.siteMeta, paddingLeft: 22 }}>
                  {s.external_ref && <span style={S.siteCode}>{s.external_ref}</span>}
                  {s.zone_name && <span>{s.zone_name}</span>}
                  {s.active_incidents > 0 && (
                    <span style={{ color: '#ef4444', fontWeight: 600, display:'flex', alignItems:'center', gap:2 }}><AlertTriangle size={11}/> {s.active_incidents}</span>
                  )}
                </div>
                {s.address && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.3, paddingLeft: 22 }}>
                    {s.address.slice(0, 45)}{s.address.length > 45 ? '…' : ''}
                  </div>
                )}
              </div>
              {isExpanded && (
                <div style={{ marginLeft: 14, borderLeft: '1px dashed var(--border)' }}>
                  {!nvrs && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '4px 8px' }}>
                      Cargando NVRs…
                    </div>
                  )}
                  {nvrs && nvrs.length === 0 && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '4px 8px' }}>
                      {tCem('Sin NVRs en este sitio','No NVRs at this site')}
                    </div>
                  )}
                  {nvrs && nvrs.map(n => (
                    <NvrBranch
                      key={n.id}
                      nvr={n}
                      token={token}
                      onPickToCell={onPickToCell}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── ChannelsPanel ────────────────────────────────────────────────────────────

function ChannelsPanel({ site, nvrs, nvr, channels, onSelectNvr, onSelectChannel, selectedCh, token }) {
  if (!site) return (
    <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>
      {tCem('Selecciona un sitio para ver las cámaras disponibles','Select a site to see available cameras')}
    </div>
  )

  if (nvrs.length === 0) return (
    <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>
      Sin NVRs configurados para <strong>{site.name}</strong>.<br />
      <span style={{ fontSize: 11 }}>Configura NVRs en Configuración → NVRs</span>
    </div>
  )

  return (
    <div>
      <div style={S.section}>
        <div style={S.sectionTitle}>NVR</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {nvrs.map(n => (
            <div
              key={n.id}
              style={{
                padding: '7px 9px', borderRadius: 6, cursor: 'pointer',
                background: nvr?.id === n.id ? 'var(--bg-active)' : 'var(--bg-root)',
                border: `1px solid ${nvr?.id === n.id ? 'var(--accent)' : 'var(--border)'}`,
              }}
              onClick={() => onSelectNvr(n)}
            >
              <div style={{ fontSize: 12, fontWeight: 600 }}>{n.name}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                {n.brand.toUpperCase()} · {n.host} · {n.channels} canales
              </div>
            </div>
          ))}
        </div>
      </div>

      {nvr && channels.length > 0 && (
        <div style={S.section}>
          <div style={S.sectionTitle}>Canales</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {channels.map(c => (
              <button
                key={c.channel}
                style={{
                  padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
                  fontSize: 11, fontWeight: 600,
                  background: selectedCh === c.channel ? 'var(--accent)' : 'var(--bg-muted)',
                  color: selectedCh === c.channel ? '#fff' : 'var(--text-primary)',
                }}
                onClick={() => onSelectChannel(c.channel)}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {nvr && selectedCh && (
        <div style={S.section}>
          <div style={S.sectionTitle}>Vista previa</div>
          <div style={{ background: '#000', borderRadius: 6, overflow: 'hidden', aspectRatio: '16/9' }}>
            <img
              key={`${nvr.id}-${selectedCh}`}
              src={`/api/nvr/${nvr.id}/snapshot/${selectedCh}?token=${encodeURIComponent(token)}`}
              alt="preview"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none' }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── RecordingsPanel ──────────────────────────────────────────────────────────

function RecordingsPanel({ nvr, channel, token, onPlay, channelName }) {
  const today = new Date().toISOString().slice(0, 10)
  const [mode,     setMode]     = useState('single')
  const [dateStr,  setDate]     = useState(today)
  const [dateFrom, setDateFrom] = useState(today)
  const [dateTo,   setDateTo]   = useState(today)
  const [hourFrom, setHourFrom] = useState(0)
  const [hourTo,   setHourTo]   = useState(23)
  const [startH,   setStartH]   = useState('00:00')
  const [endH,     setEndH]     = useState('23:59')
  const [result,   setResult]   = useState(null)
  const [loading,  setLoading]  = useState(false)
  const [activeRec, setActive]  = useState(null)

  const search = async () => {
    if (!nvr) return
    setLoading(true)
    setResult(null)
    setActive(null)
    try {
      if (mode === 'single') {
        const url = `/api/nvr/${nvr.id}/recordings?channel=${channel}&start=${dateStr}T${startH}:00&end=${dateStr}T${endH}:59`
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        if (res.ok) setResult({ type: 'single', recordings: await res.json() })
      } else {
        const url = `/api/nvr/${nvr.id}/recordings/segments?channel=${channel}&date_from=${dateFrom}&date_to=${dateTo}&hour_from=${hourFrom}&hour_to=${hourTo}`
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        if (res.ok) setResult({ type: 'range', ...(await res.json()) })
      }
    } finally {
      setLoading(false)
    }
  }

  const play = (rec) => {
    const key = `${rec.start}-${rec.end}`
    setActive(key)
    onPlay({ start: rec.start, end: rec.end })
  }

  const RecRow = ({ rec, depth = 0 }) => {
    const key = `${rec.start}-${rec.end}`
    return (
      <div style={{ ...S.recRow(activeRec === key), marginLeft: depth * 8 }} onClick={() => play(rec)}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>▶ {fmtDt(rec.start)}</div>
        <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'var(--text-muted)' }}>
          <span>→ {fmtDt(rec.end)}</span>
          {rec.start && rec.end && (
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>⏱ {fmtDur(rec.start, rec.end)}</span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {[['single', 'Un día'], ['range', 'Rango + horario']].map(([m, label]) => (
          <button
            key={m}
            style={{
              flex: 1, padding: '6px 4px', borderRadius: 6, border: 'none',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: mode === m ? 'var(--accent)' : 'var(--bg-muted)',
              color: mode === m ? '#fff' : 'var(--text-muted)',
            }}
            onClick={() => { setMode(m); setResult(null) }}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'single' && (
        <>
          <div style={{ marginBottom: 8 }}>
            <label style={S.label}>Fecha</label>
            <input type="date" value={dateStr} onChange={e => setDate(e.target.value)} style={S.input} />
          </div>
          <div style={S.row2}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Desde</label>
              <input type="time" value={startH} onChange={e => setStartH(e.target.value)} style={S.input} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Hasta</label>
              <input type="time" value={endH} onChange={e => setEndH(e.target.value)} style={S.input} />
            </div>
          </div>
        </>
      )}

      {mode === 'range' && (
        <>
          <div style={S.row2}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Fecha inicio</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={S.input} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Fecha término</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={S.input} />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={S.label}>
              Ventana horaria:{' '}
              <strong style={{ color: 'var(--text-primary)' }}>
                {String(hourFrom).padStart(2,'0')}:00 → {String(hourTo).padStart(2,'0')}:59
              </strong>
            </label>
            <div style={S.row2}>
              <div style={{ flex: 1 }}>
                <label style={{ ...S.label, marginBottom: 2 }}>Hora inicio</label>
                <input type="range" min={0} max={23} value={hourFrom}
                  onChange={e => setHourFrom(Number(e.target.value))} style={{ width: '100%' }} />
                <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {String(hourFrom).padStart(2,'0')}:00
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ ...S.label, marginBottom: 2 }}>Hora término</label>
                <input type="range" min={0} max={23} value={hourTo}
                  onChange={e => setHourTo(Number(e.target.value))} style={{ width: '100%' }} />
                <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {String(hourTo).padStart(2,'0')}:59
                </div>
              </div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center' }}>
              Busca solo en esa ventana horaria para cada día del rango
            </div>
          </div>
        </>
      )}

      <button style={{ ...S.btnPrimary, marginBottom: 12 }} onClick={search} disabled={loading || !nvr}>
        {loading ? 'Buscando…' : '🔍 Buscar grabaciones'}
      </button>

      {channelName && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
          Canal: <strong style={{ color: 'var(--text-primary)' }}>{channelName}</strong>
        </div>
      )}

      {result?.type === 'single' && (
        result.recordings.length === 0
          ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11, padding: '12px 0' }}>Sin grabaciones en ese período</div>
          : result.recordings.map((r, i) => <RecRow key={i} rec={r} />)
      )}

      {result?.type === 'range' && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
            {result.total} grabación{result.total !== 1 ? 'es' : ''} en {result.days?.length} día{result.days?.length !== 1 ? 's' : ''}
          </div>
          {result.days?.map(day => (
            <div key={day.date} style={{ marginBottom: 10 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: 'var(--text-primary)',
                marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                📅 {day.date}
                <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>
                  {day.hour_from}:00–{day.hour_to}:59 · {day.recordings.length} clip{day.recordings.length !== 1 ? 's' : ''}
                </span>
              </div>
              {day.recordings.length === 0
                ? <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '4px 0 4px 8px' }}>Sin grabaciones</div>
                : day.recordings.map((r, i) => <RecRow key={i} rec={r} depth={1} />)
              }
            </div>
          ))}
        </div>
      )}

      {activeRec && (
        <button style={{ ...S.btnSecondary, width: '100%', marginTop: 6 }}
          onClick={() => { setActive(null); onPlay(null) }}>
          ✕ Volver a vista en vivo
        </button>
      )}
    </div>
  )
}

// ─── AnalyticsPanel ──────────────────────────────────────────────────────────

// Cap del rango de ventana de procesamiento. Si tocás, ajustá también
// JOB_MAX_WINDOW_SECONDS en backend/api/analytics_jobs.py y
// MAX_WINDOW_SECONDS en retaliq-edge/edge/jobs/executor.py.
const JOB_MAX_WINDOW_MIN = 60

function AnalyticsPanel({ nvr, channel, channelName, siteId, token }) {
  const now = new Date()
  const today = now.toISOString().slice(0, 10)
  // Default: última hora completa. Si son las 14:25, sugerimos 13:00 → 14:00.
  const currentH = now.getHours()
  const defaultHourFrom = Math.max(0, currentH - 1)
  const defaultHourTo   = currentH
  const [dateFrom, setDateFrom] = useState(today)
  const [dateTo,   setDateTo]   = useState(today)
  const [hourFrom, setHourFrom] = useState(defaultHourFrom)
  const [hourTo,   setHourTo]   = useState(defaultHourTo)
  const [selected, setSelected] = useState(['lpr'])
  const [label,    setLabel]    = useState('')
  const [jobs,     setJobs]     = useState([])
  const [creating, setCreating] = useState(false)
  const [tab,      setTab]      = useState('new')

  // Validación de ventana: calcular duración y enforce el cap.
  const windowMinutes = (() => {
    try {
      const s = new Date(`${dateFrom}T${String(hourFrom).padStart(2,'0')}:00:00`)
      const e = new Date(`${dateTo}T${String(hourTo).padStart(2,'0')}:59:59`)
      const m = Math.ceil((e - s) / 60000)
      return m > 0 ? m : 0
    } catch { return 0 }
  })()
  const windowExceeds = windowMinutes > JOB_MAX_WINDOW_MIN
  const windowInvalid = windowMinutes <= 0

  const loadJobs = useCallback(async () => {
    const r = await fetch('/api/analytics-jobs/?limit=20', { headers: { Authorization: `Bearer ${token}` } })
    if (r.ok) setJobs(await r.json())
  }, [token])

  useEffect(() => { loadJobs() }, [loadJobs])

  const toggleAnalytic = (key) =>
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])

  const submit = async () => {
    if (!nvr || selected.length === 0) return
    setCreating(true)
    try {
      const r = await fetch('/api/analytics-jobs/', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nvr_id   : nvr.id,
          channel,
          date_from: dateFrom,
          date_to  : dateTo,
          hour_from: hourFrom,
          hour_to  : hourTo,
          analytics: selected,
          label    : label || `${channelName || `CH${channel}`} · ${dateFrom}${dateFrom !== dateTo ? `→${dateTo}` : ''} · ${hourFrom}h-${hourTo}h`,
          site_id  : siteId,
        }),
      })
      if (r.ok) { setTab('history'); setTimeout(loadJobs, 300) }
    } finally {
      setCreating(false)
    }
  }

  const cancelJob = async (jobId) => {
    await fetch(`/api/analytics-jobs/${jobId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    loadJobs()
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {[['new', '+ Nuevo job'], ['history', `Historial (${jobs.length})`]].map(([t, l]) => (
          <button key={t} style={{
            flex: 1, padding: '6px 4px', borderRadius: 6, border: 'none',
            fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: tab === t ? 'var(--accent)' : 'var(--bg-muted)',
            color: tab === t ? '#fff' : 'var(--text-muted)',
          }} onClick={() => { setTab(t); if (t === 'history') loadJobs() }}>{l}</button>
        ))}
      </div>

      {tab === 'new' && (
        <>
          {!nvr ? (
            <div style={{ padding: '12px 0', color: 'var(--text-muted)', fontSize: 11, textAlign: 'center' }}>
              Selecciona un sitio, NVR y canal primero
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 10, padding: '7px 9px', borderRadius: 6, background: 'var(--bg-muted)', fontSize: 11 }}>
                <strong>{nvr.name}</strong> · {channelName || `Canal ${channel}`}
              </div>

              <div style={S.row2}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Fecha inicio</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={S.input} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Fecha término</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={S.input} />
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={S.label}>
                  Ventana horaria:{' '}
                  <strong style={{ color: 'var(--text-primary)' }}>
                    {String(hourFrom).padStart(2,'0')}:00 → {String(hourTo).padStart(2,'0')}:59
                  </strong>
                </label>
                <div style={S.row2}>
                  <div style={{ flex: 1 }}>
                    <input type="range" min={0} max={23} value={hourFrom}
                      onChange={e => setHourFrom(Number(e.target.value))} style={{ width: '100%' }} />
                    <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 700 }}>{String(hourFrom).padStart(2,'0')}h</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <input type="range" min={0} max={23} value={hourTo}
                      onChange={e => setHourTo(Number(e.target.value))} style={{ width: '100%' }} />
                    <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 700 }}>{String(hourTo).padStart(2,'0')}h</div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={S.label}>Analíticas a ejecutar</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
                  {ANALYTIC_OPTS.map(opt => (
                    <div key={opt.key} style={{
                      padding: '7px 9px', borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${selected.includes(opt.key) ? 'var(--accent)' : 'var(--border)'}`,
                      background: selected.includes(opt.key) ? 'var(--bg-active)' : 'var(--bg-root)',
                    }} onClick={() => toggleAnalytic(opt.key)}>
                      <div style={{ fontSize: 16, marginBottom: 2 }}>{opt.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>{opt.label}</div>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={S.label}>Descripción (opcional)</label>
                <input style={S.input} placeholder="Ej: Revisión acceso nocturno"
                  value={label} onChange={e => setLabel(e.target.value)} />
              </div>

              {/* Indicador de duración de la ventana y validación */}
              <div style={{
                marginBottom: 10, padding: '6px 9px', borderRadius: 6,
                fontSize: 10, fontWeight: 600,
                background: windowExceeds
                  ? '#ef444422'
                  : windowInvalid ? '#f59e0b22' : 'var(--bg-muted)',
                color: windowExceeds
                  ? '#ef4444'
                  : windowInvalid ? '#f59e0b' : 'var(--text-muted)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>Ventana de procesamiento</span>
                <span>
                  {windowMinutes} min {windowExceeds && `· máx ${JOB_MAX_WINDOW_MIN}`}
                  {windowInvalid && ' · rango inválido'}
                </span>
              </div>

              <button style={{
                ...S.btnPrimary,
                opacity: (selected.length === 0 || creating || windowExceeds || windowInvalid) ? 0.55 : 1
              }}
                disabled={selected.length === 0 || creating || windowExceeds || windowInvalid}
                onClick={submit}
                title={
                  windowExceeds ? `Ventana excede ${JOB_MAX_WINDOW_MIN} min — acotá el rango`
                  : windowInvalid ? 'El rango termina antes de empezar'
                  : ''
                }
              >
                {creating ? 'Enviando…' : <><FlaskConical size={13} style={{display:'inline',verticalAlign:'middle',marginRight:5}}/> Iniciar análisis retroactivo</>}
              </button>

              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center', lineHeight: 1.4 }}>
                El Mac mini M4 del sitio procesará las grabaciones.<br />
                Resultados en el feed de detecciones.
              </div>
            </>
          )}
        </>
      )}

      {tab === 'history' && (
        <div>
          {jobs.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11, padding: '20px 0' }}>
              Sin jobs creados aún
            </div>
          ) : jobs.map(job => (
            <div key={job.id} style={S.jobRow}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, flex: 1, paddingRight: 6 }}>
                  {job.label || `Job #${job.id}`}
                </div>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 8, flexShrink: 0,
                  background: STATUS_COLOR[job.status] + '22', color: STATUS_COLOR[job.status],
                }}>
                  {STATUS_LABEL[job.status]}
                </span>
              </div>

              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>
                {job.date_from}{job.date_from !== job.date_to ? ` → ${job.date_to}` : ''}{' '}
                · {String(job.hour_from).padStart(2,'0')}h–{String(job.hour_to).padStart(2,'0')}h
              </div>

              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                {(job.analytics || []).map(a => {
                  const opt = ANALYTIC_OPTS.find(o => o.key === a)
                  return (
                    <span key={a} style={{
                      fontSize: 9, padding: '1px 5px', borderRadius: 6,
                      background: 'var(--bg-muted)', color: 'var(--text-muted)', fontWeight: 600,
                    }}>{opt ? `${opt.icon} ${opt.label}` : a}</span>
                  )
                })}
              </div>

              {job.status === 'running' && (
                <div>
                  <div style={{ height: 4, borderRadius: 2, background: '#1a1a1a', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${job.progress}%`, background: '#3b82f6', borderRadius: 2, transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 9, color: '#3b82f6', marginTop: 2 }}>
                    {job.progress}% · {job.results_count} resultados
                  </div>
                </div>
              )}
              {job.status === 'completed' && (
                <div style={{ fontSize: 10, color: '#10b981', fontWeight: 600, marginTop: 2 }}>
                  ✓ {job.results_count} resultado{job.results_count !== 1 ? 's' : ''} encontrado{job.results_count !== 1 ? 's' : ''}
                </div>
              )}
              {job.status === 'failed' && (
                <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2 }}>✗ {job.error_msg || 'Error'}</div>
              )}
              {job.status === 'pending' && (
                <button style={{ ...S.btnSecondary, fontSize: 9, padding: '2px 8px', marginTop: 4 }}
                  onClick={() => cancelJob(job.id)}>Cancelar</button>
              )}
              {job.status === 'running' && (
                <button style={{
                  fontSize: 9, padding: '3px 10px', marginTop: 4, border: 'none', cursor: 'pointer',
                  borderRadius: 4, background: '#ef444422', color: '#ef4444', fontWeight: 600,
                }}
                  onClick={() => {
                    if (confirm('¿Detener este análisis? El M4 va a abortar limpio en los próximos segundos.')) {
                      cancelJob(job.id)
                    }
                  }}>⏹ Detener análisis</button>
              )}
            </div>
          ))}
          <button style={{ ...S.btnSecondary, width: '100%', marginTop: 4, fontSize: 11 }} onClick={loadJobs}>
            ↺ Actualizar
          </button>
        </div>
      )}
    </div>
  )
}

// ─── GridCell: celda del grid central con drop handler ──────────────────────

function GridCell({ idx, cell, selected, playback, token, onDrop, onClear, onSelect, singleCell, pulsing, quality, siteId, siteName = '', operatorName = '', onQualityChange = null }) {
  const [over, setOver] = useState(false)
  const [nvr, setNvr]   = useState(null)

  // Para renderizar el stream necesitamos un objeto nvr-like con .id (CameraCell lo usa).
  useEffect(() => {
    if (cell) setNvr({ id: cell.nvrId, name: '' })
    else setNvr(null)
  }, [cell?.nvrId])

  const handleDragOver = (e) => {
    if (!e.dataTransfer.types.includes('application/x-retaliq-camera')) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    if (!over) setOver(true)
  }
  const handleDragLeave = () => setOver(false)
  const handleDrop = (e) => {
    e.preventDefault()
    setOver(false)
    const raw = e.dataTransfer.getData('application/x-retaliq-camera')
    if (!raw) return
    try { onDrop(JSON.parse(raw)) } catch {}
  }

  if (!cell) {
    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          ...S.cell(false),
          background: over ? '#0a2540' : '#080808',
          border: over ? '2px dashed var(--accent)' : '1px solid var(--border)',
          transition: 'background 0.1s',
        }}>
        <div style={{ ...S.empty, background: 'transparent', gap: 4 }}>
          <span style={{ fontSize: 22, color: '#1a1a1a' }}>+</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center' }}>
            {over ? 'Soltar cámara aquí' : 'Vacío'}
          </span>
          <span style={{ fontSize: 9, color: '#222' }}>celda {idx + 1}</span>
        </div>
      </div>
    )
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ position: 'relative', ...S.cell(selected), border: over ? '2px dashed var(--accent)' : undefined }}
    >
      <CameraCell
        nvr={nvr}
        channel={cell.channel}
        label={cell.name}
        selected={selected}
        onSelect={() => onSelect?.(cell)}
        playback={playback}
        token={token}
        singleCell={singleCell}
        pulsing={pulsing}
        quality={quality}
        onQualityChange={onQualityChange}
        siteId={siteId}
        siteName={siteName}
        operatorName={operatorName}
      />
      <button
        onClick={(e) => { e.stopPropagation(); onClear() }}
        style={{
          position: 'absolute', top: 4, left: 4, zIndex: 5,
          width: 20, height: 20, borderRadius: 4, border: 'none', cursor: 'pointer',
          background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 12, lineHeight: 1,
        }}
        title="Quitar de esta celda"
      >×</button>
    </div>
  )
}

// ─── DroppableEmpty: celda vacía del modo "primeros N canales" con drop ──────

function DroppableEmpty({ idx, onDrop }) {
  const [over, setOver] = useState(false)
  return (
    <div
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('application/x-retaliq-camera')) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
        if (!over) setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setOver(false)
        const raw = e.dataTransfer.getData('application/x-retaliq-camera')
        if (!raw) return
        try { onDrop(JSON.parse(raw)) } catch {}
      }}
      style={{
        ...S.cell(false),
        background: over ? '#0a2540' : '#080808',
        border: over ? '2px dashed var(--accent)' : undefined,
      }}>
      <div style={{ ...S.empty, background: 'transparent' }}>
        <span style={{ color: over ? 'var(--accent)' : '#1a1a1a' }}>{over ? '↓' : '—'}</span>
      </div>
    </div>
  )
}

// ─── Main MonitorView ─────────────────────────────────────────────────────────

function LiveMonitor({ wsMessage }) {
  // Activity en REF (no state). El problema anterior: cada wsMessage que
  // llegaba del WS (heartbeats + edge_events del tracker = decenas por minuto)
  // disparaba setActivity → re-render del LiveMonitor → cada GridCell creaba
  // nuevos callbacks por prop → CameraCell.useEffect detectaba refs nuevas →
  // CADA STREAM MJPEG SE CERRABA Y REABRÍA. A 5+ ws/s eso saturaba el browser.
  //
  // Ahora la ref se actualiza silenciosamente y un tick de 1s dispara UN
  // re-render controlado. Re-render rate desacoplado del wsMessage rate.
  const activityRef = useRef({})
  const [, bumpActivity] = useReducer(x => x + 1, 0)

  useEffect(() => {
    if (!wsMessage) return
    const t = wsMessage
    const cam = t.camera_name || t.camera
    if (!cam) return
    if (t.type === 'edge_event' || t.type === 'alert' || t.event_type) {
      activityRef.current = { ...activityRef.current, [cam]: Date.now() }
    }
  }, [wsMessage])

  // Tick controlado: 1s. Re-render solo si HAY cambio (entrada nueva o
  // vencida desde el último render).
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now()
      const prev = activityRef.current
      const next = {}
      let changed = false
      for (const k in prev) {
        if (now - prev[k] < 2000) next[k] = prev[k]
        else changed = true
      }
      if (Object.keys(prev).length !== Object.keys(next).length) changed = true
      if (changed) {
        activityRef.current = next
        bumpActivity()
      }
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const activity = activityRef.current

  // Mapeo cámara→channel para resolver actividad
  // El WS event trae camera_name="Cámara 5" pero CameraCell renderiza por
  // channel. Necesitamos saber qué channel le toca a cada nombre del site activo.
  const [siteCams, setSiteCams] = useState([])
  // Helper que pasa por debajo a CameraCell para que sepa si pulsa.
  const isPulsing = useCallback((channel) => {
    const cam = siteCams.find(c => c.channel === channel)
    if (!cam) return false
    return !!activity[cam.name]
  }, [activity, siteCams])
  const token = useAuthStore(s => s.token)
  const userEmail = useAuthStore(s => s.user?.email || '')

  // Per-cell quality override. Mapeo cellKey → 'rtc' | 'main' | 'sub'.
  // cellKey = `${nvrId}-${channel}` para celdas del grid mix, o `${nvrId}-${channel}`
  // también para el grid de un solo sitio. Si una key NO está, hereda del global
  // quality. Esto permite tener HD en una cell y RTC en otra simultáneamente.
  const [cellQualities, setCellQualities] = useState({})
  const setCellQuality = useCallback((key, q) => {
    setCellQualities(prev => ({ ...prev, [key]: q }))
  }, [])
  const getCellQuality = useCallback((key, globalQ) => {
    return cellQualities[key] || globalQ
  }, [cellQualities])

  const [activeSite, setActiveSite] = useState(null)
  const [siteNvrs,   setSiteNvrs]   = useState([])
  const [activeNvr,  setActiveNvr]  = useState(null)
  const [channels,   setChannels]   = useState([])
  const [layout,     setLayout]     = useState(LAYOUTS[1])
  const [selectedCh, setSelectedCh] = useState(1)
  const [rightTab,   setRightTab]   = useState('cameras')
  const [playback,   setPlayback]   = useState(null)
  // Calidad de stream del grid Vivo. Compartido con MapView Q1 vía localStorage.
  // 'rtc' = WebRTC direct M4 (sub-segundo, Tailscale)
  // 'main' = MJPEG main stream (HD, ~300ms)
  // 'sub'  = MJPEG sub stream (SD, ahorra banda — default en multi-grid)
  // 'auto' = main si 1×1, sub si multi (comportamiento histórico)
  const [quality, setQuality] = useState(() => {
    try { return localStorage.getItem(QUALITY_KEY) || 'auto' }
    catch { return 'auto' }
  })
  useEffect(() => {
    try { localStorage.setItem(QUALITY_KEY, quality) } catch {}
  }, [quality])
  // Layout libre del grid central: cada celda puede tener {nvrId, channel, name, online}
  // o estar vacía. Persiste en localStorage para que el operador vuelva a su layout.
  const [cellsLayout, setCellsLayout] = useState(() => {
    try {
      const raw = localStorage.getItem('retaliq_monitor_cells')
      const arr = raw ? JSON.parse(raw) : []
      return Array.isArray(arr) ? arr : []
    } catch { return [] }
  })

  // Persistir cellsLayout
  useEffect(() => {
    try { localStorage.setItem('retaliq_monitor_cells', JSON.stringify(cellsLayout)) } catch {}
  }, [cellsLayout])

  const selectNvr = useCallback(async (nvr) => {
    setActiveNvr(nvr)
    setChannels([])
    setPlayback(null)
    const r = await fetch(`/api/nvr/${nvr.id}/channels`, { headers: { Authorization: `Bearer ${token}` } })
    if (r.ok) {
      const chs = await r.json()
      setChannels(chs)
      setSelectedCh(chs[0]?.channel || 1)
    }
  }, [token])

  const selectSite = useCallback(async (site) => {
    setActiveSite(site)
    setActiveNvr(null)
    setChannels([])
    setPlayback(null)
    setSiteNvrs([])

    const r = await fetch('/api/nvr/configs', { headers: { Authorization: `Bearer ${token}` } })
    if (r.ok) {
      const all = await r.json()
      const forSite = all.filter(n => n.site_id === site.id)
      setSiteNvrs(forSite)
      if (forSite.length > 0) selectNvr(forSite[0])
    }
  }, [token, selectNvr])

  // Cargar resolved-cameras del sitio activo (nombres del remote_config) —
  // necesario para resolver actividad WS por channel.
  useEffect(() => {
    if (!activeSite?.id || !token) {
      setSiteCams([])
      return
    }
    fetch(`/api/analytics/cameras?site_id=${activeSite.id}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setSiteCams)
      .catch(() => setSiteCams([]))
  }, [activeSite?.id, token])

  // Cargar canales de un NVR específico bajo demanda (para el grid drag/drop)
  const ensureNvrChannels = useCallback(async (nvrId) => {
    // Si activeNvr ya es ese, los channels ya están en state
    if (activeNvr?.id === nvrId) return
    // Buscar config completa del NVR
    const r = await fetch('/api/nvr/configs', { headers: { Authorization: `Bearer ${token}` } })
    if (!r.ok) return
    const all = await r.json()
    const nvr = all.find(n => n.id === nvrId)
    if (nvr) selectNvr(nvr)
  }, [activeNvr, token, selectNvr])

  // Llena la primera celda vacía del grid con la cámara dada (click en tree)
  const pickToCell = useCallback((cam) => {
    setCellsLayout(prev => {
      const arr = Array.from({ length: layout.count }, (_, i) => prev[i] || null)
      const emptyIdx = arr.findIndex(c => !c)
      const idx = emptyIdx >= 0 ? emptyIdx : 0  // si grid lleno, reemplaza la primera
      arr[idx] = cam
      return arr
    })
    ensureNvrChannels(cam.nvrId)
    setSelectedCh(cam.channel)
  }, [layout, ensureNvrChannels])

  // Drop sobre una celda específica del grid
  const dropOnCell = useCallback((idx, cam) => {
    setCellsLayout(prev => {
      const arr = Array.from({ length: layout.count }, (_, i) => prev[i] || null)
      arr[idx] = cam
      return arr
    })
    ensureNvrChannels(cam.nvrId)
    setSelectedCh(cam.channel)
  }, [layout, ensureNvrChannels])

  // Vaciar una celda específica
  const clearCell = useCallback((idx) => {
    setCellsLayout(prev => {
      const arr = Array.from({ length: layout.count }, (_, i) => prev[i] || null)
      arr[idx] = null
      return arr
    })
  }, [layout])

  // Limpiar todo el grid
  const clearGrid = useCallback(() => setCellsLayout([]), [])

  // ─── Mosaicos guardados + link compartible (VMS-6) ───────────────────────
  //
  // Mosaico = snapshot del estado actual del Monitor:
  //   { name, ts, siteId, layout, quality, cellsLayout, cellQualities }
  //
  // Persistido en localStorage bajo `retaliq_monitor_mosaics` (array). El
  // operador puede guardar, cargar y borrar mosaicos. El link compartible
  // codifica el mismo objeto en base64-url y se pone en query string `?m=`
  // para que se pueda abrir directo desde Slack/mail.
  const MOSAICS_KEY = 'retaliq_monitor_mosaics'
  const [mosaics, setMosaics] = useState(() => {
    try { return JSON.parse(localStorage.getItem(MOSAICS_KEY) || '[]') } catch { return [] }
  })
  const [mosaicMenuOpen, setMosaicMenuOpen] = useState(false)
  const persistMosaics = useCallback((arr) => {
    setMosaics(arr)
    try { localStorage.setItem(MOSAICS_KEY, JSON.stringify(arr)) } catch {}
  }, [])
  const currentMosaicState = useCallback(() => ({
    siteId: activeSite?.id || null,
    layoutLabel: layout.label,
    quality,
    cellsLayout,
    cellQualities,
  }), [activeSite, layout, quality, cellsLayout, cellQualities])
  const applyMosaicState = useCallback((m) => {
    if (!m) return
    const lyt = LAYOUTS.find(l => l.label === m.layoutLabel) || LAYOUTS[1]
    setLayout(lyt)
    if (m.quality) setQuality(m.quality)
    if (m.cellsLayout) setCellsLayout(m.cellsLayout)
    if (m.cellQualities) setCellQualities(m.cellQualities)
    // siteId no se setea aquí — se hace via deep-link en mount
  }, [])
  const saveCurrentMosaic = useCallback(() => {
    const name = (window.prompt('Nombre del mosaico:') || '').trim()
    if (!name) return
    const state = currentMosaicState()
    const item = { name, ts: Date.now(), ...state }
    const next = [item, ...mosaics.filter(m => m.name !== name)]
    persistMosaics(next)
    setMosaicMenuOpen(false)
  }, [currentMosaicState, mosaics, persistMosaics])
  const deleteMosaic = useCallback((name) => {
    persistMosaics(mosaics.filter(m => m.name !== name))
  }, [mosaics, persistMosaics])
  const shareCurrentLink = useCallback(async () => {
    const state = currentMosaicState()
    const enc = btoa(unescape(encodeURIComponent(JSON.stringify(state))))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
    const url = `${window.location.origin}/monitor?m=${enc}`
    try {
      await navigator.clipboard.writeText(url)
      alert('Link copiado al portapapeles. Cualquier operador autenticado lo puede abrir y verá la misma vista.')
    } catch {
      window.prompt('Copia este link manualmente:', url)
    }
  }, [currentMosaicState])

  // Aplicar mosaico desde query string al montar
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search)
      const enc = p.get('m')
      if (!enc) return
      const json = decodeURIComponent(escape(atob(
        enc.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(enc.length/4)*4, '=')
      )))
      const state = JSON.parse(json)
      applyMosaicState(state)
      // No limpiamos el URL para que el operador pueda re-compartirlo
    } catch (e) {
      console.warn('[mosaic] decode fail:', e.message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const chName = (ch) => channels.find(c => c.channel === ch)?.name || `CH${String(ch).padStart(2,'0')}`

  // Visualización del grid: prioriza cellsLayout. Si está vacío, mantiene el
  // comportamiento clásico (primeros N canales del activeNvr) para no romper UX
  // a los usuarios que vienen del flujo anterior.
  const hasCustomCells = cellsLayout.some(c => c)
  const visibleChannels = Array.from(
    { length: layout.count }, (_, i) => i + 1
  ).filter(ch => ch <= (activeNvr?.channels || 0))

  return (
    <div style={S.root}>

      {/* ── Panel izquierdo: sitios + tree de cámaras ── */}
      <SitePanel
        activeSite={activeSite}
        onSelect={selectSite}
        token={token}
        onPickToCell={pickToCell}
      />

      {/* ── Centro: grid de cámaras ── */}
      <div style={S.center}>
        <div style={S.toolbar}>
          {activeSite ? (
            <>
              <span style={{ fontSize: 12, fontWeight: 700 }}>{activeSite.name}</span>
              {activeSite.external_ref && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
                  background: 'var(--bg-muted)', color: 'var(--text-muted)' }}>
                  {activeSite.external_ref}
                </span>
              )}
              {activeNvr && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {activeNvr.name}</span>}
            </>
          ) : (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>← Selecciona un sitio</span>
          )}
          <div style={{ flex: 1 }} />
          {/* Toggle calidad — solo Vivo (en Reproducción siempre MJPEG playback) */}
          {!playback && (
            <>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Calidad:</span>
              <div style={{
                display: 'flex', borderRadius: 7, overflow: 'hidden',
                // Borde y sombra externa fuertes para que el grupo "RTC HD SD"
                // se vea como un control diferenciado del toolbar (Paulo es
                // daltónico — no podemos depender solo de tono, necesitamos
                // contraste de forma y luminosidad).
                border: '1.5px solid #94A3B8',
                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
              }}>
                {[
                  { id: 'rtc',  label: 'RTC', tip: 'WebRTC direct edge↔operador, sub-segundo (Tailscale)' },
                  { id: 'main', label: 'HD',  tip: 'HLS direct M4, 1080p (~4-6s lag)' },
                  { id: 'sub',  label: 'SD',  tip: 'MJPEG sub stream vía cloud, ahorra banda' },
                ].map((q, i, arr) => {
                  const active = quality === q.id
                  return (
                    <button
                      key={q.id}
                      title={q.tip}
                      onClick={() => setQuality(q.id)}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#CBD5E1' }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#E2E8F0' }}
                      style={{
                        minWidth: 52, padding: '8px 14px',
                        fontSize: 12, fontWeight: 800,
                        border: 'none',
                        borderRight: i < arr.length - 1 ? '1.5px solid #94A3B8' : 'none',
                        cursor: 'pointer',
                        // Inactivo: gris medio sólido (#E2E8F0) bien distinguible del fondo blanco.
                        // Activo: accent azul + texto blanco + sombra interna sutil.
                        background: active ? 'var(--accent)' : '#E2E8F0',
                        color: active ? '#fff' : '#1E293B',
                        letterSpacing: '0.04em',
                        transition: 'background 0.12s',
                        boxShadow: active ? 'inset 0 1px 3px rgba(0,0,0,0.25)' : 'none',
                      }}
                      aria-pressed={active}
                    >{q.label}</button>
                  )
                })}
              </div>
              {/* Aviso: RTC con grid >1 abre N sesiones WebRTC simultáneas */}
              {quality === 'rtc' && layout.count > 1 && (
                <span title="Cada cell abre su propia sesión WebRTC al M4" style={{
                  fontSize: 10, color: '#f59e0b', fontWeight: 600,
                }}>⚠ {layout.count} sesiones</span>
              )}
            </>
          )}
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Grid:</span>
          {LAYOUTS.map(l => (
            <button key={l.label} style={{
              padding: '4px 8px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600,
              background: layout.label === l.label ? 'var(--accent)' : 'var(--bg-muted)',
              color: layout.label === l.label ? '#fff' : 'var(--text-muted)',
            }} onClick={() => setLayout(l)}>{l.label}</button>
          ))}
          {playback && (
            <button style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, background: '#f59e0b22', color: '#f59e0b' }}
              onClick={() => setPlayback(null)}>⏹ Vivo</button>
          )}
          {hasCustomCells && (
            <button style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, background: 'var(--bg-muted)', color: 'var(--text-muted)' }}
              onClick={clearGrid} title="Vaciar todas las celdas">🗑 Vaciar grilla</button>
          )}
          {/* VMS-6: Mosaicos guardados + compartir link */}
          <div style={{ position: 'relative' }}>
            <button
              style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, background: 'var(--bg-muted)', color: 'var(--text-primary)' }}
              onClick={() => setMosaicMenuOpen(v => !v)}
              title="Mosaicos guardados — guardar vista actual o cargar una existente"
              aria-label="Abrir menú de mosaicos"
            >📋 Mosaicos {mosaics.length > 0 && <span style={{ color: 'var(--text-muted)' }}>({mosaics.length})</span>}</button>
            {mosaicMenuOpen && (
              <div style={{
                position: 'absolute', top: 30, right: 0, zIndex: 20,
                background: 'rgba(15,15,15,0.98)', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 6, padding: 4, minWidth: 260,
                boxShadow: '0 6px 20px rgba(0,0,0,0.7)',
              }}>
                <button
                  onClick={saveCurrentMosaic}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#fff',
                    background: 'var(--accent)', border: 'none', cursor: 'pointer',
                    borderRadius: 4, marginBottom: 4,
                  }}
                >💾 Guardar vista actual como mosaico</button>
                {mosaics.length === 0 && (
                  <div style={{
                    padding: '10px 6px', fontSize: 10, color: '#888', textAlign: 'center',
                  }}>Sin mosaicos guardados todavía</div>
                )}
                {mosaics.map(m => (
                  <div key={m.name} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '2px 0',
                  }}>
                    <button
                      onClick={() => { applyMosaicState(m); setMosaicMenuOpen(false) }}
                      style={{
                        flex: 1, textAlign: 'left',
                        padding: '6px 8px', fontSize: 11, color: '#fff',
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        borderRadius: 4,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      title={`Layout ${m.layoutLabel} · ${m.quality?.toUpperCase()} · ${m.cellsLayout?.filter(c=>c).length || 0} cámaras`}
                    >
                      <div style={{ fontWeight: 600 }}>{m.name}</div>
                      <div style={{ fontSize: 9, color: '#888' }}>
                        {m.layoutLabel} · {String(m.quality || '').toUpperCase()} · {(m.cellsLayout?.filter(c=>c).length || 0)} cámaras
                      </div>
                    </button>
                    <button
                      onClick={() => deleteMosaic(m.name)}
                      title="Borrar mosaico"
                      style={{
                        width: 24, height: 24, borderRadius: 4,
                        background: 'transparent', color: '#ef4444',
                        border: 'none', cursor: 'pointer', fontSize: 14,
                      }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, background: 'var(--bg-muted)', color: 'var(--text-primary)' }}
            onClick={shareCurrentLink}
            title="Copiar link compartible — abre la misma vista en otra sesión"
            aria-label="Compartir link de la vista actual"
          >🔗 Compartir</button>
        </div>

        <div style={S.gridWrap}>
          {hasCustomCells ? (
            <div style={S.grid(layout.cols)}>
              {Array.from({ length: layout.count }, (_, i) => i).map(idx => {
                const cell = cellsLayout[idx]
                return (
                  <GridCell
                    key={`cell-${idx}`}
                    idx={idx}
                    cell={cell}
                    selected={cell && selectedCh === cell.channel && activeNvr?.id === cell.nvrId}
                    playback={cell && selectedCh === cell.channel ? playback : null}
                    token={token}
                    singleCell={layout.count === 1}
                    pulsing={cell ? isPulsing(cell.channel) : false}
                    quality={cell ? getCellQuality(`${cell.nvrId}-${cell.channel}`, quality) : quality}
                    onQualityChange={cell ? (q) => setCellQuality(`${cell.nvrId}-${cell.channel}`, q) : null}
                    siteId={activeSite?.id}
                    siteName={activeSite?.name || ''}
                    operatorName={userEmail}
                    onDrop={(cam) => dropOnCell(idx, cam)}
                    onClear={() => clearCell(idx)}
                    onSelect={(cam) => {
                      ensureNvrChannels(cam.nvrId)
                      setSelectedCh(cam.channel)
                      setRightTab('recordings')
                    }}
                  />
                )
              })}
            </div>
          ) : !activeNvr ? (
            <div style={S.empty}>
              <span style={{ fontSize: 48 }}>📹</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {activeSite ? 'Sin NVRs configurados en este sitio' : 'Selecciona un sitio'}
              </span>
              <span style={{ fontSize: 12, textAlign: 'center' }}>
                {activeSite
                  ? 'Expandí el sitio en la izquierda y arrastrá cámaras a la grilla, o click para auto-llenar.'
                  : 'Usa el buscador de la izquierda para encontrar una tienda'}
              </span>
            </div>
          ) : (
            <div style={S.grid(layout.cols)}>
              {visibleChannels.map(ch => (
                <CameraCell
                  key={`${activeNvr.id}-${ch}`}
                  nvr={activeNvr}
                  channel={ch}
                  label={chName(ch)}
                  selected={selectedCh === ch}
                  onSelect={(c) => { setSelectedCh(c); setRightTab('recordings') }}
                  playback={selectedCh === ch ? playback : null}
                  token={token}
                  singleCell={layout.count === 1}
                  pulsing={isPulsing(ch)}
                  quality={getCellQuality(`${activeNvr.id}-${ch}`, quality)}
                  onQualityChange={(q) => setCellQuality(`${activeNvr.id}-${ch}`, q)}
                  siteId={activeSite?.id}
                  siteName={activeSite?.name || ''}
                  operatorName={userEmail}
                />
              ))}
              {Array.from({ length: layout.count - visibleChannels.length }).map((_, i) => (
                <DroppableEmpty
                  key={`empty-${i}`}
                  idx={visibleChannels.length + i}
                  onDrop={(cam) => dropOnCell(visibleChannels.length + i, cam)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Panel derecho ── */}
      <div style={S.rightPanel}>
        <div style={S.tabs}>
          {[
            ['cameras',    <><Camera size={12}/> Cámaras</>],
            ['recordings', <><History size={12}/> Grabaciones</>],
            ['analytics',  <><FlaskConical size={12}/> Analíticas</>],
          ].map(([t, l]) => (
            <button key={t} style={S.tab(rightTab === t)} onClick={() => setRightTab(t)}>{l}</button>
          ))}
        </div>
        <div style={S.panelBody}>
          {rightTab === 'cameras' && (
            <ChannelsPanel
              site={activeSite} nvrs={siteNvrs} nvr={activeNvr}
              channels={channels} onSelectNvr={selectNvr}
              onSelectChannel={(ch) => { setSelectedCh(ch); setRightTab('recordings') }}
              selectedCh={selectedCh} token={token}
            />
          )}
          {rightTab === 'recordings' && (
            <RecordingsPanel
              nvr={activeNvr} channel={selectedCh}
              channelName={chName(selectedCh)} token={token} onPlay={setPlayback}
            />
          )}
          {rightTab === 'analytics' && (
            <AnalyticsPanel
              nvr={activeNvr} channel={selectedCh}
              channelName={chName(selectedCh)} siteId={activeSite?.id} token={token}
            />
          )}
        </div>
      </div>
    </div>
  )
}


// ═════════════════════════════════════════════════════════════════════════════
// PlaybackMonitor — Tab B: reproducción forense de grabaciones
// ═════════════════════════════════════════════════════════════════════════════
//
// Una sola cámara grande, sin tree. Selector de cámara/fecha/hora arriba,
// player de playback abajo. A la derecha, eventos del periodo seleccionado
// con click-to-jump (salta el player al timestamp del evento).
//
// Por ahora el "salto" reconstruye la URL de playback con un nuevo start;
// cuando agreguemos scrubber, se podrá saltar sin recargar el stream.

// PlaybackVideo — <video> nativo con MP4 fragmentado streaming desde
// /api/nvr/{id}/playback_mp4. Reemplaza el viejo PlaybackImg MJPEG.
// Ventajas: arranque más rápido, seek instantáneo, controles nativos,
// fullscreen, picture-in-picture, audio si el NVR lo expone.
//
// Cuando paused=true, pausamos el <video> en el frame actual (que SÍ
// preserva). No necesitamos congelar en canvas como con MJPEG.
function PlaybackVideo({ nvr, channel, start, end, token, paused = false, siteId = null }) {
  const videoRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [elapsedSec, setElapsedSec] = useState(0)
  const [err, setErr] = useState(null)
  const [sensorsGrant, setSensorsGrant] = useState(null)
  const startedAtRef = useRef(Date.now())

  // Si el sitio tiene M4 host directo (SITE_CONFIG), pedimos un grant de
  // scope=sensors al backend y usamos ese token para pegar directo al
  // sidecar del M4. Esto resuelve sitios donde Hetzner no puede llegar al
  // NVR por conflictos de subnets Tailscale (ej. Latadia y Cat System
  // ambos en 192.168.1.0/24).
  const m4Host = siteId ? getSiteM4Host(siteId) : null
  useEffect(() => {
    if (!m4Host || !siteId || !token) return
    let cancelled = false
    fetch(`/api/sensors/${siteId}/grant`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(g => { if (!cancelled && g) setSensorsGrant(g) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [m4Host, siteId, token])

  // URL del stream MP4. Cambia con cada nuevo rango.
  const src = (!nvr || !channel || !start || !end) ? '' : (
    (m4Host && sensorsGrant?.token)
      ? `${sensorsGrant.base_url}/nvr/${nvr.id}/playback_mp4/${channel}` +
        `?token=${encodeURIComponent(sensorsGrant.token)}` +
        `&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
      : `/api/nvr/${nvr.id}/playback_mp4/${channel}?token=${encodeURIComponent(token)}` +
        `&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
  )

  // Pausar/reanudar el video según prop paused
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (paused) v.pause()
    else if (v.readyState >= 2) v.play().catch(()=>{})
  }, [paused])

  // Reset loader cuando cambia el src
  useEffect(() => {
    setLoading(true)
    setElapsedSec(0)
    setErr(null)
    startedAtRef.current = Date.now()
  }, [src])

  // Timer mientras carga
  useEffect(() => {
    if (!loading || paused) return
    const id = setInterval(() => {
      setElapsedSec((Date.now() - startedAtRef.current) / 1000)
    }, 200)
    return () => clearInterval(id)
  }, [loading, paused])

  if (!nvr || !channel) {
    return (
      <div style={{ ...S.empty, background: '#000', aspectRatio: '16/9' }}>
        <span style={{ fontSize: 36, opacity: 0.3 }}>📼</span>
        <span style={{ fontSize: 12, color: '#555' }}>Elegí cámara y rango</span>
      </div>
    )
  }

  return (
    <div style={{
      position: 'relative', width: '100%', aspectRatio: '16/9',
      background: '#000', borderRadius: 8, overflow: 'hidden',
      border: '1px solid var(--border)',
    }}>
      <video
        ref={videoRef}
        src={src}
        autoPlay
        playsInline
        controls
        muted
        onLoadedData={() => setLoading(false)}
        onCanPlay={() => setLoading(false)}
        onError={(e) => {
          const code = e.currentTarget?.error?.code
          setErr(`Error de video (code ${code || '?'})`)
          setLoading(false)
        }}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          display: 'block', background: '#000',
        }}
      />
      {loading && !err && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.78)',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 10, color: '#e5e5e5', pointerEvents: 'none',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff',
            animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>Cargando video…</span>
          <span style={{
            fontSize: 11, color: '#94a3b8',
            fontVariantNumeric: 'tabular-nums', fontFamily: 'monospace',
          }}>{elapsedSec.toFixed(1)}s</span>
        </div>
      )}
      {err && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 8, color: '#fca5a5',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{err}</span>
          <span style={{ fontSize: 10, color: '#94a3b8' }}>
            Probá un rango más corto o cambiá de cámara
          </span>
        </div>
      )}
      <div style={{
        position: 'absolute', top: 8, left: 8,
        background: 'rgba(0,0,0,0.7)',
        color: paused ? '#94a3b8' : '#f59e0b',
        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
        letterSpacing: '0.05em', pointerEvents: 'none',
      }}>● REC · {start.replace('T', ' ')} → {end.replace('T', ' ')}</div>
    </div>
  )
}


function PlaybackImg({ nvr, channel, start, end, token, paused = false }) {
  const imgRef = useRef(null)
  const canvasRef = useRef(null)   // congela el último frame al pausar
  const retryRef = useRef(null)
  const attemptRef = useRef(0)
  const [retrying, setRetrying] = useState(false)
  // Loader inicial — ffmpeg tarda 2-6s en abrir RTSP y entregar primer frame.
  // En vez de pantalla negra, mostramos progreso visible.
  const [loading, setLoading] = useState(true)
  const [elapsedSec, setElapsedSec] = useState(0)
  const startedAtRef = useRef(Date.now())

  const streamUrl = useCallback((bust = false) => {
    if (!nvr || !channel || !start || !end) return ''
    const auth = `?token=${encodeURIComponent(token)}`
    const cb   = bust ? `&_=${Date.now()}` : ''
    return `/api/nvr/${nvr.id}/playback/${channel}${auth}` +
           `&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}` +
           `&fps=15&width=1280${cb}`
  }, [nvr, channel, start, end, token])

  const clearRetry = useCallback(() => {
    if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null }
  }, [])
  const openStream = useCallback((bust = false) => {
    if (!imgRef.current) return
    setRetrying(false)
    imgRef.current.src = streamUrl(bust)
  }, [streamUrl])
  const scheduleRetry = useCallback(() => {
    if (paused) return   // si estamos pausados, no reintentamos solos
    clearRetry()
    attemptRef.current += 1
    const delay = Math.min(8000, 1000 * 2 ** (attemptRef.current - 1))
    setRetrying(true)
    retryRef.current = setTimeout(() => openStream(true), delay)
  }, [clearRetry, openStream, paused])

  useEffect(() => {
    clearRetry()
    attemptRef.current = 0
    setRetrying(false)
    // Reset del loader cada vez que cambia el stream
    if (!paused) {
      setLoading(true)
      setElapsedSec(0)
      startedAtRef.current = Date.now()
    }
    // Si está pausado: capturamos el último frame del <img> a un <canvas>
    // ANTES de cortar el stream. Eso nos deja un still visible mientras
    // el backend cierra ffmpeg y libera el NVR. Al despausar, el useEffect
    // se vuelve a disparar (paused cambia) y reabrimos el stream.
    if (paused) {
      const img = imgRef.current
      const canvas = canvasRef.current
      if (img && canvas && img.naturalWidth > 0 && img.naturalHeight > 0) {
        canvas.width  = img.naturalWidth
        canvas.height = img.naturalHeight
        try {
          // El endpoint MJPEG es same-origin (proxy del backend), no debería
          // tinttear el canvas. Si lo hace por alguna razón, caemos al else
          // y mostramos el overlay PAUSA sobre el último frame del <img>.
          canvas.getContext('2d').drawImage(img, 0, 0)
        } catch (e) {
          console.warn('No se pudo congelar frame al canvas:', e)
        }
      }
      // Cortar el stream para liberar el ffmpeg del backend.
      if (img) img.src = ''
      return () => clearRetry()
    }
    if (imgRef.current) {
      imgRef.current.src = ''
      const t = setTimeout(() => openStream(false), 60)
      return () => { clearTimeout(t); clearRetry() }
    }
    return () => clearRetry()
  }, [streamUrl, openStream, clearRetry, paused])

  function onLoadFrame() {
    if (attemptRef.current !== 0) attemptRef.current = 0
    if (retrying) setRetrying(false)
    if (loading) setLoading(false)
  }

  // Timer del loader inicial — actualiza cada 200ms el contador "Xs" visible
  useEffect(() => {
    if (!loading || paused) return
    const id = setInterval(() => {
      const s = (Date.now() - startedAtRef.current) / 1000
      setElapsedSec(s)
    }, 200)
    return () => clearInterval(id)
  }, [loading, paused])

  if (!nvr || !channel) {
    return (
      <div style={{ ...S.empty, background: '#000', aspectRatio: '16/9' }}>
        <span style={{ fontSize: 36, opacity: 0.3 }}>📼</span>
        <span style={{ fontSize: 12, color: '#555' }}>Elegí cámara y rango</span>
      </div>
    )
  }

  return (
    <div style={{
      position: 'relative', width: '100%', aspectRatio: '16/9',
      background: '#000', borderRadius: 8, overflow: 'hidden',
      border: '1px solid var(--border)',
    }}>
      <img
        ref={imgRef}
        alt="playback"
        onLoad={onLoadFrame}
        onError={scheduleRetry}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          display: paused ? 'none' : 'block',
        }}
      />
      {/* Frame congelado al pausar — se posiciona absoluto encima del <img>
          y se oculta cuando el stream está en vivo. */}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          display: paused ? 'block' : 'none',
          position: 'absolute', top: 0, left: 0,
        }}
      />
      {retrying && !paused && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 6, color: '#e5e5e5', pointerEvents: 'none',
        }}>
          <div style={{
            width: 24, height: 24, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.25)', borderTopColor: '#fff',
            animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 11 }}>{tCem('Reconectando…','Reconnecting…')}</span>
        </div>
      )}
      {loading && !retrying && !paused && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.78)',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 10, color: '#e5e5e5', pointerEvents: 'none',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff',
            animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>Abriendo grabación…</span>
          <span style={{
            fontSize: 11, color: '#94a3b8',
            fontVariantNumeric: 'tabular-nums', fontFamily: 'monospace',
          }}>
            {elapsedSec.toFixed(1)}s
          </span>
          <span style={{ fontSize: 10, color: '#64748b', maxWidth: '70%', textAlign: 'center' }}>
            Conectando RTSP del NVR · primera apertura suele tardar 2–6s
          </span>
        </div>
      )}
      {paused && (
        <div style={{
          position: 'absolute', top: 12, right: 12,
          background: 'rgba(0,0,0,0.7)', color: '#fff',
          padding: '4px 10px', borderRadius: 4,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
          display: 'flex', alignItems: 'center', gap: 6,
          pointerEvents: 'none',
        }}>
          <span style={{ fontSize: 14, lineHeight: 1 }}>⏸</span> PAUSA
        </div>
      )}
      <div style={{
        position: 'absolute', top: 8, left: 8,
        background: 'rgba(0,0,0,0.7)',
        color: paused ? '#94a3b8' : '#f59e0b',
        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
        letterSpacing: '0.05em',
      }}>{paused ? '⏸ PAUSA' : '●'} REC · {start.replace('T',' ')} → {end.slice(11,19)}</div>
    </div>
  )
}

function todayISO() {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 10)
}

// Timeline 24h con heatmap: 1 barra por bin (default 15 min = 96 bins).
// Altura de cada barra es proporcional al total de eventos del bin. La barra
// se colorea verde/ámbar/rojo según intensidad relativa. Hover muestra detalle,
// click salta el player a esa hora con ventana de 15 min.
function TimelineHeatmap({ siteId, channel, date, currentHour, currentWindowMin, token, onJump }) {
  const [data, setData] = useState(null)
  const [hover, setHover] = useState(null)

  useEffect(() => {
    if (!siteId || !channel || !date || !token) return
    fetch(`/api/analytics/timeline?site_id=${siteId}&channel=${channel}&date=${date}&bin_minutes=15`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData(null))
  }, [siteId, channel, date, token])

  if (!data || !data.bins) return null
  const max = data.bins.reduce((m, b) => Math.max(m, b.vehicles + b.persons), 0) || 1

  return (
    <div style={{
      marginTop: 14, background: 'var(--bg-surface)',
      border: '1px solid var(--border)', borderRadius: 8, padding: 10,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 6,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Timeline · {data.date}
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
          🚗 {data.total.vehicles} · 🚶 {data.total.persons} · bins {data.bin_minutes}min
        </span>
      </div>
      {/* Barras */}
      <div style={{
        position: 'relative', display: 'flex', alignItems: 'flex-end',
        height: 60, gap: 1, background: '#0a0a0a',
        borderRadius: 4, padding: '4px 4px 0 4px', overflow: 'hidden',
      }}>
        {data.bins.map((b, i) => {
          const total = b.vehicles + b.persons
          const h = max > 0 ? (total / max) * 100 : 0
          const intense = total / max
          const color = total === 0 ? '#1a1a1a'
                       : intense > 0.66 ? '#ef4444'
                       : intense > 0.33 ? '#f59e0b'
                       : '#10b981'
          // Marcar el rango actualmente visible en el player
          const [hh, mm] = b.start.split(':').map(Number)
          const binStartMin = hh * 60 + mm
          const playStartMin = currentHour * 60
          const playEndMin   = playStartMin + currentWindowMin
          const inRange = binStartMin >= playStartMin && binStartMin < playEndMin

          return (
            <div
              key={i}
              onClick={() => {
                onJump(hh, 15)
              }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{
                flex: 1, minWidth: 2,
                height: `${Math.max(h, total > 0 ? 4 : 1)}%`,
                background: color,
                opacity: hover === i ? 1 : (inRange ? 1 : 0.85),
                outline: inRange ? '1px solid #3b82f6' : 'none',
                cursor: 'pointer', borderRadius: '1px 1px 0 0',
                transition: 'opacity 0.1s, height 0.2s',
              }}
              title={`${b.start} · ${b.vehicles}🚗 ${b.persons}🚶`}
            />
          )
        })}
      </div>
      {/* Eje horario */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginTop: 4, fontSize: 8, color: 'var(--text-muted)',
      }}>
        {[0, 3, 6, 9, 12, 15, 18, 21, 24].map(h => (
          <span key={h}>{String(h).padStart(2,'0')}h</span>
        ))}
      </div>
      {/* Detalle del hover */}
      {hover !== null && data.bins[hover] && (
        <div style={{
          marginTop: 6, fontSize: 10, color: 'var(--text-secondary)',
          textAlign: 'center',
        }}>
          {data.bins[hover].start} · 🚗 {data.bins[hover].vehicles}
          {' '}· 🚶 {data.bins[hover].persons}
          {' '}— click para saltar
        </div>
      )}
    </div>
  )
}

function PlaybackMonitor() {
  const token = useAuthStore(s => s.token)
  // Deep-link desde la galería forense:
  //   ?site=<int>&camera_name=<str>&date=YYYY-MM-DD&hour=<0-23>&window=<5|15|30|60>
  // Si están presentes, se pre-cargan en el state inicial y se limpian
  // del URL para no contaminar futuros refreshes.
  const _deepLink = (() => {
    const p = new URLSearchParams(window.location.search)
    if (p.get('tab') !== 'playback') return null
    return {
      site:        Number(p.get('site') || 0) || null,
      camera_name: p.get('camera_name') || null,
      date:        p.get('date') || null,
      hour:        p.get('hour') ? Number(p.get('hour')) : null,
      window:      p.get('window') ? Number(p.get('window')) : null,
    }
  })()
  const [sites, setSites] = useState([])
  const [siteId, setSiteId] = useState(_deepLink?.site || null)
  const [nvrs, setNvrs] = useState([])
  const [nvr, setNvr] = useState(null)
  const [channels, setChannels] = useState([])
  const [chNum, setChNum] = useState(1)
  // VMS-7: canales adicionales para reproducción sincronizada (sin chNum)
  // El total a mostrar = [chNum, ...syncChs]. Un solo timeline controla todos.
  const [syncChs, setSyncChs] = useState([])
  const [date, setDate] = useState(_deepLink?.date || todayISO())
  const [hour, setHour] = useState(_deepLink?.hour ?? new Date().getHours())
  const [windowMin, setWindowMin] = useState(_deepLink?.window || 60)
  const [events, setEvents] = useState([])
  // Pre-selección de cámara por name — se resuelve a chNum cuando llegan
  // los channels del backend. Una vez aplicado se nulea.
  const [pendingCameraName, setPendingCameraName] = useState(_deepLink?.camera_name || null)

  // Sitios — guard contra token no hidratado
  useEffect(() => {
    if (!token) return
    fetch('/api/sites/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(arr => {
        setSites(arr || [])
        if (arr?.[0]?.id && !siteId) setSiteId(arr[0].id)
      })
      .catch(err => console.error('PlaybackMonitor sites fetch error:', err))
  }, [token, siteId])

  // NVRs del sitio
  useEffect(() => {
    if (!siteId || !token) return
    fetch('/api/nvr/configs', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(all => {
        const f = (all || []).filter(n => n.site_id === siteId)
        setNvrs(f); setNvr(f[0] || null)
      })
      .catch(err => console.error('PlaybackMonitor nvrs fetch error:', err))
  }, [siteId, token])

  // Canales — desde /api/analytics/cameras (nombres del remote_config, no
  // los del firmware NVR que suelen ser "Camera 01" repetidos).
  useEffect(() => {
    if (!siteId || !token) return
    fetch(`/api/analytics/cameras?site_id=${siteId}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(chs => {
        const list = chs || []
        setChannels(list)
        // Deep-link de la galería forense: si vino camera_name, resolverlo
        // a channel. Caer al primer canal si no hay match.
        if (pendingCameraName) {
          const m = list.find(c => c.name === pendingCameraName)
          setChNum(m?.channel || list[0]?.channel || 1)
          setPendingCameraName(null)
        } else {
          setChNum(prev => prev || list[0]?.channel || 1)
        }
      })
      .catch(err => console.error('PlaybackMonitor channels fetch error:', err))
  }, [siteId, token, pendingCameraName])

  // ─── Controles de reproducción ─────────────────────────────────────
  //
  // El MJPEG stream del backend transmite del NVR desde start hasta end.
  // No es un video controlable nativamente (no podemos pause/seek con
  // <video>). Simulamos controles cliente-side:
  //   - Pause: limpia el src del <img> (queda último frame visible)
  //   - Play:  re-pone el src
  //   - ±10s/±30s: ajusta offsetSec, lo que recarga el stream con un
  //                start desplazado y se ve como un "salto" tipo seek
  //
  // Cuando cambia date/hour/windowMin manualmente, reseteamos offset.
  const [paused, setPaused]       = useState(false)
  const [offsetSec, setOffsetSec] = useState(0)
  // Marcadores in/out para export — segundos relativos al inicio del rango
  const [markIn,  setMarkIn]  = useState(null)
  const [markOut, setMarkOut] = useState(null)
  // Bookmarks persistentes (V8-7) — flags clickeables sobre el scrubber
  const [bookmarks, setBookmarks] = useState([])
  const [narrateOpen, setNarrateOpen] = useState(false)
  useEffect(() => {
    setOffsetSec(0); setPaused(false)
    setMarkIn(null); setMarkOut(null)
    setSyncChs([])
  }, [date, hour, windowMin, siteId, chNum])

  // Cargar bookmarks del rango activo
  useEffect(() => {
    if (!siteId || !token) return
    const startISOq = new Date(`${date}T${String(hour).padStart(2,'0')}:00:00Z`).toISOString()
    const endISOq   = new Date(new Date(startISOq).getTime() + windowMin * 60000).toISOString()
    const params = new URLSearchParams({
      site_id: String(siteId), channel: String(chNum),
      start: startISOq, end: endISOq, limit: '50',
    })
    fetch(`/api/bookmarks?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : { items: [] })
      .then(j => setBookmarks(j.items || []))
      .catch(() => setBookmarks([]))
  }, [siteId, chNum, date, hour, windowMin, token])

  // Guardar bookmark en posición actual
  const addBookmark = useCallback(async (color = 'yellow') => {
    if (!siteId) return
    const note = window.prompt('Nota (opcional):') || ''
    const startISOq = new Date(`${date}T${String(hour).padStart(2,'0')}:00:00Z`).getTime()
    const bmTs = new Date(startISOq + offsetSec * 1000).toISOString()
    try {
      const r = await fetch('/api/bookmarks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          site_id: siteId, nvr_id: nvr?.id, channel: chNum,
          ts: bmTs, note: note || null, color,
        }),
      })
      const bm = await r.json()
      if (r.ok) setBookmarks(prev => [...prev, bm])
    } catch (e) { console.warn('[bookmark]', e) }
  }, [siteId, chNum, nvr, date, hour, offsetSec, token])

  // Borrar bookmark
  const removeBookmark = useCallback(async (id) => {
    try {
      await fetch(`/api/bookmarks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      setBookmarks(prev => prev.filter(b => b.id !== id))
    } catch {}
  }, [token])

  // Rango efectivo (con offset aplicado). offset > 0 = forward, < 0 = rewind.
  const baseStartDate = new Date(`${date}T${String(hour).padStart(2,'0')}:00:00Z`)
  const startEffectiveDate = new Date(baseStartDate.getTime() + offsetSec * 1000)
  const endEffectiveDate   = new Date(startEffectiveDate.getTime() + windowMin * 60000)
  const startISO = startEffectiveDate.toISOString().slice(0,19)
  const endISO   = endEffectiveDate.toISOString().slice(0,19)

  const offsetLabel = (() => {
    const s = Math.abs(offsetSec)
    const m = Math.floor(s / 60), sec = s % 60
    const sign = offsetSec >= 0 ? '+' : '-'
    if (offsetSec === 0) return 'al inicio del rango'
    if (m === 0) return `${sign}${sec}s`
    return `${sign}${m}m ${sec}s`
  })()

  // Eventos en la ventana
  useEffect(() => {
    if (!siteId) return
    const cam = channels.find(c => c.channel === chNum)
    // En playback queremos los eventos REALES del periodo de la grabación.
    // Eso requeriría que cada evento traiga "event_ts" en la ventana exacta.
    fetch(`/api/analytics/detections?site_id=${siteId}&limit=200&window_minutes=${Math.max(60, Math.ceil((Date.now() - new Date(startISO).getTime())/60000) + windowMin)}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(j => {
        const startMs = new Date(startISO + 'Z').getTime()
        const endMs   = new Date(endISO + 'Z').getTime()
        const filtered = (j.items || []).filter(e => {
          const t = new Date(e.ts).getTime()
          return t >= startMs && t <= endMs && (!cam || e.camera_name === undefined || true)
        })
        setEvents(filtered)
      })
  }, [siteId, chNum, startISO, endISO, channels, token, windowMin])

  const cam = channels.find(c => c.channel === chNum)
  const camName = cam ? `CH${String(chNum).padStart(2,'0')} · ${cam.name}` : `CH${String(chNum).padStart(2,'0')}`

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Centro */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 16, overflow: 'auto', minWidth: 0 }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <select value={siteId || ''} onChange={e => setSiteId(Number(e.target.value))} style={pbSel}>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {nvrs.length > 1 && (
            <select value={nvr?.id || ''} onChange={e => setNvr(nvrs.find(n => n.id === Number(e.target.value)))} style={pbSel}>
              {nvrs.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
            </select>
          )}
          <select value={chNum} onChange={e => setChNum(Number(e.target.value))} style={pbSel}>
            {channels.map(c => <option key={c.channel} value={c.channel}>
              CH{String(c.channel).padStart(2,'0')} · {c.name}
            </option>)}
          </select>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={pbSel} />
          <select value={hour} onChange={e => setHour(Number(e.target.value))} style={pbSel}>
            {Array.from({length: 24}, (_, i) => i).map(h => (
              <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>
            ))}
          </select>
          <select value={windowMin} onChange={e => setWindowMin(Number(e.target.value))} style={pbSel}>
            <option value={5}>5 min</option>
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={60}>1 h</option>
          </select>
        </div>

        {/* VMS-7: Selector multi-cámara para reproducción sincronizada */}
        <div style={{
          marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>
            Comparar sincronizado:
          </span>
          {channels.map(c => {
            const isMain = c.channel === chNum
            const isSync = syncChs.includes(c.channel)
            const active = isMain || isSync
            return (
              <button
                key={c.channel}
                onClick={() => {
                  if (isMain) return // no podés desmarcar el principal
                  setSyncChs(prev => prev.includes(c.channel)
                    ? prev.filter(ch => ch !== c.channel)
                    : [...prev, c.channel].slice(0, 3) // max 4 total (1 + 3)
                  )
                }}
                disabled={isMain}
                style={{
                  padding: '3px 8px', borderRadius: 12,
                  fontSize: 10, fontWeight: 600,
                  border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
                  background: isMain ? 'var(--accent)' : isSync ? 'rgba(59,130,246,0.2)' : 'var(--bg-root)',
                  color: isMain ? '#fff' : isSync ? 'var(--accent)' : 'var(--text-muted)',
                  cursor: isMain ? 'default' : 'pointer',
                }}
                title={isMain ? 'Canal principal — no se puede quitar' : isSync ? 'Click para quitar' : 'Click para agregar'}
              >
                {isMain && '★ '}{`CH${String(c.channel).padStart(2,'0')}`} {active && '✓'}
              </button>
            )
          })}
          {syncChs.length > 0 && (
            <button
              onClick={() => setSyncChs([])}
              style={{
                padding: '3px 8px', borderRadius: 12, fontSize: 10,
                background: 'transparent', color: '#888', border: '1px solid var(--border)',
                cursor: 'pointer',
              }}
              title="Limpiar canales adicionales"
            >✕ Limpiar</button>
          )}
        </div>

        {/* Player(s) — grid sincronizado si hay >1, single si solo el principal */}
        <div style={{ maxWidth: 1200, width: '100%' }}>
          {(() => {
            const allChs = [chNum, ...syncChs]
            if (allChs.length === 1) {
              return (
                <PlaybackVideo
                  nvr={nvr}
                  channel={chNum}
                  start={startISO}
                  end={endISO}
                  token={token}
                  paused={paused}
                  siteId={siteId}
                />
              )
            }
            const cols = allChs.length === 2 ? 2 : 2
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 6,
                aspectRatio: `${cols * 16} / ${Math.ceil(allChs.length/cols) * 9}`,
              }}>
                {allChs.map(ch => {
                  const camN = channels.find(c => c.channel === ch)
                  return (
                    <div key={ch} style={{ position: 'relative', background: '#000', borderRadius: 4, overflow: 'hidden' }}>
                      <PlaybackVideo
                        nvr={nvr}
                        channel={ch}
                        start={startISO}
                        end={endISO}
                        token={token}
                        paused={paused}
                        siteId={siteId}
                      />
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                        color: '#fff', fontSize: 11, fontWeight: 600,
                        padding: '14px 8px 5px 8px',
                      }}>
                        {ch === chNum && '★ '}{camN ? `CH${String(ch).padStart(2,'0')} · ${camN.name}` : `CH${String(ch).padStart(2,'0')}`}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })()}

          {/* Controles tipo VCR. ±s ajustan offsetSec lo que recarga el
              stream del backend desde un start desplazado — equivalente
              cliente-side a un seek "casi-instantáneo" (los frames nuevos
              tardan ~2-3s en aparecer porque el backend reabre RTSP). */}
          <div style={{
            marginTop: 8,
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 10px',
            background: 'var(--bg-surface)',
            borderRadius: 6, border: '1px solid var(--border)',
            flexWrap: 'wrap',
          }}>
            <button title="Volver al inicio del rango"
                    onClick={() => { setOffsetSec(0); setPaused(false) }}
                    style={pbBtn}>⏮</button>
            <button title="Retroceder 30s"
                    onClick={() => setOffsetSec(o => o - 30)}
                    style={pbBtn}>⏪ 30s</button>
            <button title="Retroceder 10s"
                    onClick={() => setOffsetSec(o => o - 10)}
                    style={pbBtn}>◀ 10s</button>
            <button title={paused ? 'Reanudar' : 'Pausar'}
                    onClick={() => setPaused(p => !p)}
                    style={{ ...pbBtn,
                             background: paused ? 'var(--accent)' : 'var(--bg-muted)',
                             color: paused ? '#fff' : 'var(--text-primary)',
                             fontSize: 14, padding: '6px 12px' }}>
              {paused ? '▶' : '⏸'}
            </button>
            <button title="Avanzar 10s"
                    onClick={() => setOffsetSec(o => o + 10)}
                    style={pbBtn}>10s ▶</button>
            <button title="Avanzar 30s"
                    onClick={() => setOffsetSec(o => o + 30)}
                    style={pbBtn}>30s ⏩</button>
            <span style={{
              flex: 1,
              fontSize: 11, color: 'var(--text-muted)',
              fontFamily: 'monospace', textAlign: 'right',
            }}>
              {paused ? '⏸ PAUSA · ' : ''}{offsetLabel}
            </span>
          </div>

          {/* Scrubber visual: barra clickable mapeando posición a offsetSec
              dentro del rango [0, windowMin*60]. El offset se aplica al
              start del rango recargando el stream desde un instante exacto. */}
          {(() => {
            const totalSec = windowMin * 60
            const pos = Math.max(0, Math.min(totalSec, offsetSec))
            const pct = totalSec > 0 ? (pos / totalSec) * 100 : 0
            const fmtMS = (s) => {
              const m = Math.floor(s/60), sec = s%60
              return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
            }
            const onScrubClick = (e) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const x = e.clientX - rect.left
              const p = Math.max(0, Math.min(1, x / rect.width))
              const sec = Math.round(p * totalSec)
              setOffsetSec(sec)
              setPaused(false)
            }
            return (
              <div style={{
                marginTop: 8,
                background: 'var(--bg-surface)',
                borderRadius: 6, border: '1px solid var(--border)',
                padding: '10px 12px 12px 12px',
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 10, color: 'var(--text-muted)',
                  fontFamily: 'monospace', marginBottom: 4,
                }}>
                  <span>00:00</span>
                  <span>{paused ? '⏸ ' : ''}{fmtMS(pos)} / {fmtMS(totalSec)}</span>
                  <span>{fmtMS(totalSec)}</span>
                </div>
                {/* Barra de progreso clickeable */}
                <div
                  onClick={onScrubClick}
                  style={{
                    position: 'relative',
                    height: 28, background: '#0a0a0a',
                    borderRadius: 4, cursor: 'pointer',
                    border: '1px solid #1a1a1a',
                    overflow: 'hidden',
                  }}
                  title="Click para saltar a ese instante"
                  aria-label={`Scrubber, posición ${fmtMS(pos)} de ${fmtMS(totalSec)}`}
                >
                  {/* Tramo seleccionado in/out (verde translúcido) */}
                  {markIn != null && markOut != null && markOut > markIn && (
                    <div style={{
                      position: 'absolute', top: 0, bottom: 0,
                      left: `${(markIn / totalSec) * 100}%`,
                      width: `${((markOut - markIn) / totalSec) * 100}%`,
                      background: 'rgba(16,185,129,0.3)',
                      borderLeft: '2px solid #10b981',
                      borderRight: '2px solid #10b981',
                    }} />
                  )}
                  {/* Marker IN */}
                  {markIn != null && (
                    <div style={{
                      position: 'absolute', top: -2, bottom: -2,
                      left: `${(markIn / totalSec) * 100}%`,
                      width: 2, background: '#10b981', zIndex: 2,
                    }} title="IN" />
                  )}
                  {/* Marker OUT */}
                  {markOut != null && (
                    <div style={{
                      position: 'absolute', top: -2, bottom: -2,
                      left: `${(markOut / totalSec) * 100}%`,
                      width: 2, background: '#ef4444', zIndex: 2,
                    }} title="OUT" />
                  )}
                  {/* Posición actual (azul) */}
                  <div style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: 0, width: `${pct}%`,
                    background: 'linear-gradient(90deg, rgba(59,130,246,0.45), rgba(59,130,246,0.75))',
                    transition: 'width 0.1s',
                  }} />
                  <div style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: `${pct}%`,
                    width: 3, background: '#3b82f6',
                    boxShadow: '0 0 6px rgba(59,130,246,0.9)',
                  }} />
                  {/* Bookmarks como flags clickeables */}
                  {bookmarks.map(bm => {
                    const bmSec = (new Date(bm.ts).getTime() - baseStartDate.getTime()) / 1000
                    if (bmSec < 0 || bmSec > totalSec) return null
                    const left = (bmSec / totalSec) * 100
                    const colorMap = { red:'#ef4444', yellow:'#fbbf24', green:'#10b981', blue:'#3b82f6', purple:'#a855f7' }
                    const c = colorMap[bm.color] || '#fbbf24'
                    return (
                      <div
                        key={bm.id}
                        title={`${bm.note || '(sin nota)'} — click salta · shift+click borra`}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (e.shiftKey) {
                            if (window.confirm(`Borrar bookmark "${bm.note || bm.id}"?`)) removeBookmark(bm.id)
                          } else {
                            setOffsetSec(Math.round(bmSec))
                            setPaused(false)
                          }
                        }}
                        style={{
                          position: 'absolute', top: -6, bottom: -6,
                          left: `${left}%`,
                          width: 0, zIndex: 3, cursor: 'pointer',
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: -8, left: -7,
                          width: 0, height: 0,
                          borderLeft: '7px solid transparent',
                          borderRight: '7px solid transparent',
                          borderTop: `9px solid ${c}`,
                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                        }} />
                        <div style={{
                          position: 'absolute', top: 0, bottom: 0,
                          left: -1, width: 2, background: c, opacity: 0.7,
                        }} />
                      </div>
                    )
                  })}
                </div>
                {/* Marker / export controls */}
                <div style={{
                  marginTop: 8, display: 'flex', gap: 6,
                  alignItems: 'center', flexWrap: 'wrap',
                }}>
                  <button
                    onClick={() => addBookmark('yellow')}
                    style={{ ...pbBtn, background: '#fbbf2422', color: '#fbbf24', fontWeight: 700 }}
                    title="Guardar bookmark en la posición actual"
                  >🔖 Marcar ({bookmarks.length})</button>
                  <button
                    onClick={() => setNarrateOpen(true)}
                    style={{ ...pbBtn, background: '#a855f722', color: '#a855f7', fontWeight: 700 }}
                    title="Generar narración del segmento con IA"
                  >🎙 Narrar IA</button>
                  <button
                    onClick={() => setMarkIn(pos)}
                    style={{ ...pbBtn, background: '#10b98122', color: '#10b981', fontWeight: 700 }}
                    title="Marcar inicio de segmento (IN)"
                  >▶ IN ({markIn != null ? fmtMS(markIn) : '—'})</button>
                  <button
                    onClick={() => setMarkOut(pos)}
                    style={{ ...pbBtn, background: '#ef444422', color: '#ef4444', fontWeight: 700 }}
                    title="Marcar fin de segmento (OUT)"
                  >■ OUT ({markOut != null ? fmtMS(markOut) : '—'})</button>
                  <button
                    onClick={() => { setMarkIn(null); setMarkOut(null) }}
                    style={pbBtn}
                    title="Limpiar marcadores"
                  >✕ Limpiar</button>
                  <span style={{ flex: 1 }} />
                  {(() => {
                    const dur = (markIn != null && markOut != null) ? markOut - markIn : 0
                    const valid = markIn != null && markOut != null && markOut > markIn
                    const exportClip = async () => {
                      if (!valid || !nvr) return
                      // Construir start/end ISO en base al baseStart + markers
                      const exStartDate = new Date(baseStartDate.getTime() + markIn * 1000)
                      const exEndDate   = new Date(baseStartDate.getTime() + markOut * 1000)
                      const exStart = exStartDate.toISOString().slice(0,19)
                      const exEnd   = exEndDate.toISOString().slice(0,19)
                      const params = new URLSearchParams({
                        start: exStart, end: exEnd,
                        site_name: sites.find(s => s.id === siteId)?.name || '',
                        camera_name: cam?.name || '',
                      })
                      // Trigger download. Como necesitamos Authorization header, usamos
                      // fetch + blob + <a download>. El backend devuelve fmp4 streamado;
                      // el browser descargará a medida que llegue.
                      const url = `/api/nvr/${nvr.id}/export_mp4/${chNum}?${params.toString()}`
                      try {
                        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                        if (!r.ok) throw new Error(`HTTP ${r.status}`)
                        const blob = await r.blob()
                        const dlUrl = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = dlUrl
                        a.download = (r.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1])
                                     || `clip_${chNum}_${Date.now()}.mp4`
                        document.body.appendChild(a); a.click()
                        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(dlUrl) }, 1000)
                      } catch (e) {
                        alert(`Export falló: ${e.message}`)
                      }
                    }
                    return (
                      <button
                        onClick={exportClip}
                        disabled={!valid}
                        style={{
                          ...pbBtn,
                          background: valid ? 'var(--accent)' : 'var(--bg-muted)',
                          color: valid ? '#fff' : 'var(--text-muted)',
                          cursor: valid ? 'pointer' : 'not-allowed',
                          fontWeight: 700,
                        }}
                        title={valid
                          ? `Exportar MP4 del segmento (${fmtMS(dur)} duración) con watermark cadena de custodia`
                          : 'Marcá IN y OUT con OUT > IN para habilitar'}
                      >
                        ⬇ Exportar MP4 {valid ? `(${fmtMS(dur)})` : ''}
                      </button>
                    )
                  })()}
                </div>
              </div>
            )
          })()}

          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
            {camName} · {startISO.replace('T',' ')} → {endISO.replace('T',' ')}
          </div>

          {/* Timeline 24h con heatmap de actividad */}
          <TimelineHeatmap
            siteId={siteId}
            channel={chNum}
            date={date}
            currentHour={hour}
            currentWindowMin={windowMin}
            token={token}
            onJump={(h, win) => { setHour(h); if (win) setWindowMin(win) }}
          />
        </div>
      </div>

      {/* Lateral derecho — eventos del periodo */}
      <div style={{
        width: 340, borderLeft: '1px solid var(--border)',
        background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '10px 14px', borderBottom: '1px solid var(--border)',
          fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          Eventos en este rango ({events.length})
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 6 }}>
          {events.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
              Sin eventos en el rango
            </div>
          ) : events.map(e => {
            const p = e.payload || {}
            const meta = e.event_type.startsWith('vehicle') ? '🚗' :
                         e.event_type.startsWith('person')  ? '🚶' :
                         e.event_type === 'lpr'             ? '🪪' : '◆'
            return (
              <div key={e.id} style={{
                padding: '6px 8px', borderBottom: '1px solid var(--border)',
                cursor: 'pointer', fontSize: 11,
              }}
              onClick={() => {
                // saltar al timestamp del evento: setear hora del evento
                const t = new Date(e.ts)
                setDate(t.toISOString().slice(0,10))
                setHour(t.getHours())
                setWindowMin(15)
              }}
              onMouseEnter={ev => ev.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span><span style={{ marginRight: 4 }}>{meta}</span>{e.camera_name}</span>
                  <span style={{ fontSize: 9, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>
                    {new Date(e.ts).toLocaleTimeString('es-CL', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                  {p.vehicle_type || p.label || ''} {p.direction ? `· ${p.direction}` : ''}
                  {e.plate ? ` · ${e.plate}` : ''}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Modal narrate IA */}
      {narrateOpen && siteId && (
        <NarrativeModal
          siteId={siteId}
          channel={chNum}
          start={startISO}
          end={endISO}
          token={token}
          onClose={() => setNarrateOpen(false)}
        />
      )}
    </div>
  )
}

const pbSel = {
  fontSize: 11, padding: '5px 8px', borderRadius: 4,
  background: 'var(--bg-surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)',
}

// Botones VCR del playback. Mismo look que los selects de la toolbar pero
// con tipografía un poco más prominente para click target cómodo. El botón
// de pausa override el background/color en su style inline.
const pbBtn = {
  fontSize: 12, padding: '6px 10px', borderRadius: 4,
  background: 'var(--bg-muted)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', cursor: 'pointer',
  fontWeight: 600, minWidth: 36,
  fontFamily: 'inherit',
}


// ═════════════════════════════════════════════════════════════════════════════
// ForensicMonitor — Tab C: análisis forense con buscador de hallazgos
// ═════════════════════════════════════════════════════════════════════════════

function ForensicMonitor() {
  const token = useAuthStore(s => s.token)
  const [sites, setSites] = useState([])
  const [siteId, setSiteId] = useState(null)
  const [nvrs, setNvrs] = useState([])
  const [nvr, setNvr] = useState(null)
  const [channels, setChannels] = useState([])
  const [chNum, setChNum] = useState(1)
  const [date, setDate] = useState(todayISO())
  const [hourFrom, setHourFrom] = useState(8)
  const [hourTo, setHourTo] = useState(9)
  const [analytics, setAnalytics] = useState({ lpr: true, vehicle: true, person: true })
  const [jobId, setJobId] = useState(null)
  const [job, setJob] = useState(null)
  const [results, setResults] = useState([])
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!token) return
    fetch('/api/sites/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(arr => {
        setSites(arr || [])
        if (arr?.[0]?.id && !siteId) setSiteId(arr[0].id)
      })
      .catch(err => console.error('ForensicMonitor sites fetch error:', err))
  }, [token, siteId])
  useEffect(() => {
    if (!siteId || !token) return
    fetch('/api/nvr/configs', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(all => {
        const f = (all || []).filter(n => n.site_id === siteId)
        setNvrs(f); setNvr(f[0] || null)
      })
      .catch(err => console.error('ForensicMonitor nvrs fetch error:', err))
  }, [siteId, token])
  // Canales — desde remote_config del nodo edge (nombres reales)
  useEffect(() => {
    if (!siteId || !token) return
    fetch(`/api/analytics/cameras?site_id=${siteId}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(chs => {
        setChannels(chs || [])
        setChNum(prev => prev || chs?.[0]?.channel || 1)
      })
      .catch(err => console.error('ForensicMonitor channels fetch error:', err))
  }, [siteId, token])

  // Polling de progreso del job
  useEffect(() => {
    if (!jobId) return
    let t
    const poll = async () => {
      const r = await fetch(`/api/analytics-jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } })
      if (r.ok) {
        const j = await r.json()
        setJob(j)
        if (j.status === 'completed' || j.status === 'failed') {
          // cargar resultados (eventos en el rango del job)
          const winStart = `${j.date_from}T${String(j.hour_from).padStart(2,'0')}:00:00`
          const winEnd   = `${j.date_to}T${String(j.hour_to).padStart(2,'0')}:59:59`
          const since = new Date(winStart + 'Z').getTime()
          const until = new Date(winEnd + 'Z').getTime()
          const minutesBack = Math.max(60, Math.ceil((Date.now() - since)/60000) + 60)
          const detR = await fetch(
            `/api/analytics/detections?site_id=${siteId}&window_minutes=${minutesBack}&limit=500`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          if (detR.ok) {
            const dj = await detR.json()
            const matched = (dj.items || []).filter(e => {
              const ms = new Date(e.ts).getTime()
              return ms >= since && ms <= until
            })
            setResults(matched)
          }
          return
        }
        t = setTimeout(poll, 1500)
      }
    }
    poll()
    return () => { if (t) clearTimeout(t) }
  }, [jobId, siteId, token])

  function toggleAnalytic(k) { setAnalytics(a => ({ ...a, [k]: !a[k] })) }

  async function runAnalysis() {
    setResults([])
    const body = {
      nvr_id     : nvr.id,
      channel    : chNum,
      date_from  : date,
      date_to    : date,
      hour_from  : hourFrom,
      hour_to    : hourTo,
      analytics  : Object.entries(analytics).filter(([_,v]) => v).map(([k]) => k),
      label      : `Forense ${date} ${hourFrom}h-${hourTo}h ch${chNum}`,
      site_id    : siteId,
    }
    const r = await fetch('/api/analytics-jobs/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (r.ok) {
      const j = await r.json()
      setJobId(j.id); setJob(j)
    } else {
      alert('Error creando job: ' + (await r.text()))
    }
  }

  async function cancelJob() {
    if (!jobId) return
    await fetch(`/api/analytics-jobs/${jobId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    setJob(j => j ? { ...j, status: 'cancelled' } : j)
  }

  // Filtro de búsqueda
  const filtered = results.filter(e => {
    if (!search) return true
    const q = search.toLowerCase()
    const blob = JSON.stringify({
      type: e.event_type,
      plate: e.plate || '',
      camera: e.camera_name,
      payload: e.payload || {},
    }).toLowerCase()
    return blob.includes(q)
  })

  const stats = {
    vehicles: results.filter(e => e.event_type === 'vehicle_summary').length,
    persons : results.filter(e => e.event_type === 'person_summary').length,
    lpr     : results.filter(e => e.event_type === 'lpr').length,
    types   : results.reduce((acc, e) => {
      if (e.event_type === 'vehicle_summary') {
        const t = e.payload?.vehicle_type || 'unknown'
        acc[t] = (acc[t] || 0) + 1
      }
      return acc
    }, {}),
  }

  const minutesSpan = (hourTo + 1) * 60 - hourFrom * 60
  const exceedsHour = minutesSpan > 60

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Centro */}
      <div style={{ flex: 1, padding: 16, overflow: 'auto', minWidth: 0 }}>
        {/* Form */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 8, marginBottom: 14, padding: 12,
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 8,
        }}>
          <select value={siteId || ''} onChange={e => setSiteId(Number(e.target.value))} style={pbSel}>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {nvrs.length > 1 && (
            <select value={nvr?.id || ''} onChange={e => setNvr(nvrs.find(n => n.id === Number(e.target.value)))} style={pbSel}>
              {nvrs.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
            </select>
          )}
          <select value={chNum} onChange={e => setChNum(Number(e.target.value))} style={pbSel}>
            {channels.map(c => <option key={c.channel} value={c.channel}>
              CH{String(c.channel).padStart(2,'0')} · {c.name}
            </option>)}
          </select>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={pbSel} />
          <select value={hourFrom} onChange={e => setHourFrom(Number(e.target.value))} style={pbSel}>
            {Array.from({length: 24}, (_, i) => i).map(h => (
              <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>
            ))}
          </select>
          <select value={hourTo} onChange={e => setHourTo(Number(e.target.value))} style={pbSel}>
            {Array.from({length: 24}, (_, i) => i).map(h => (
              <option key={h} value={h}>{String(h).padStart(2,'0')}:59</option>
            ))}
          </select>
          <div style={{
            display: 'flex', gap: 6, gridColumn: 'span 2', alignItems: 'center',
          }}>
            {['lpr','vehicle','person'].map(a => (
              <label key={a} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer',
              }}>
                <input type="checkbox" checked={analytics[a]} onChange={() => toggleAnalytic(a)} />
                {a}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={runAnalysis}
              disabled={exceedsHour || (job && job.status === 'running')}
              style={{
                ...pbSel, cursor: 'pointer', fontWeight: 700,
                background: 'var(--accent)', color: '#fff', border: 'none',
                opacity: (exceedsHour || (job && job.status === 'running')) ? 0.5 : 1,
              }}
            >▶ Analizar</button>
            {job?.status === 'running' && (
              <button onClick={cancelJob} style={{ ...pbSel, background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer' }}>⏹</button>
            )}
          </div>
        </div>

        {exceedsHour && (
          <div style={{
            padding: '8px 12px', marginBottom: 10,
            background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444',
            borderRadius: 6, fontSize: 11, color: '#ef4444',
          }}>
            Ventana de {minutesSpan} min excede el máximo de 60 min permitido.
          </div>
        )}

        {/* Progreso del job */}
        {job && (
          <div style={{
            padding: 12, marginBottom: 14,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span><b>Job #{job.id}</b> · {job.status}</span>
              <span style={{ color: 'var(--text-muted)' }}>{job.results_count} resultados</span>
            </div>
            <div style={{
              marginTop: 6, height: 6, borderRadius: 3,
              background: 'var(--bg-muted)', overflow: 'hidden',
            }}>
              <div style={{
                width: `${job.progress || 0}%`, height: '100%',
                background: job.status === 'failed' ? '#ef4444'
                          : job.status === 'completed' ? '#10b981'
                          : '#3b82f6',
                transition: 'width 0.4s',
              }} />
            </div>
            {job.status === 'failed' && job.error_msg && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#ef4444' }}>{job.error_msg}</div>
            )}
          </div>
        )}

        {/* Stats */}
        {results.length > 0 && (
          <div style={{
            display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap',
          }}>
            <StatPill label="Vehículos" value={stats.vehicles} icon="🚗" />
            <StatPill label="Personas"  value={stats.persons}  icon="🚶" />
            <StatPill label="LPR"       value={stats.lpr}      icon="🪪" />
            {Object.entries(stats.types).map(([t, n]) => (
              <StatPill key={t} label={t} value={n} icon="·" small />
            ))}
          </div>
        )}

        {/* Player (cuando hay job completed mostramos el rango del job) */}
        {job?.status === 'completed' && (
          <div style={{ maxWidth: 1200, width: '100%' }}>
            <PlaybackVideo
              nvr={nvr}
              channel={chNum}
              start={`${job.date_from}T${String(job.hour_from).padStart(2,'0')}:00:00`}
              end={`${job.date_to}T${String(job.hour_to).padStart(2,'0')}:59:59`}
              token={token}
              siteId={siteId}
            />
          </div>
        )}
      </div>

      {/* Buscador a la derecha */}
      <div style={{
        width: 360, borderLeft: '1px solid var(--border)',
        background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '10px 12px', borderBottom: '1px solid var(--border)',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
          }}>Hallazgos ({filtered.length} de {results.length})</div>
          <input
            placeholder="Buscar matrícula, tipo, dirección…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box',
              fontSize: 11, padding: '6px 10px', borderRadius: 4,
              background: 'var(--bg-root)', color: 'var(--text-primary)',
              border: '1px solid var(--border)',
            }}
          />
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 6 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
              {job?.status === 'running' ? 'Procesando…' : 'Sin hallazgos'}
            </div>
          ) : filtered.map(e => {
            const p = e.payload || {}
            const meta = e.event_type.startsWith('vehicle') ? '🚗' :
                         e.event_type.startsWith('person')  ? '🚶' :
                         e.event_type === 'lpr'             ? '🪪' : '◆'
            return (
              <div key={e.id} style={{
                padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 11,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span><span style={{ marginRight: 4 }}>{meta}</span>{e.camera_name}</span>
                  <span style={{ fontSize: 9, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>
                    {new Date(e.ts).toLocaleTimeString('es-CL', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                  {p.vehicle_type || p.label || ''} {p.direction ? `· ${p.direction}` : ''}
                  {p.duration_s ? ` · ${p.duration_s.toFixed(1)}s` : ''}
                  {e.plate ? ` · placa ${e.plate}` : ''}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function StatPill({ label, value, icon, small }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: small ? '4px 8px' : '6px 12px',
      borderRadius: small ? 10 : 14,
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      fontSize: small ? 10 : 11,
    }}>
      <span>{icon}</span>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <b style={{ color: 'var(--text-primary)' }}>{value}</b>
    </div>
  )
}


// ═════════════════════════════════════════════════════════════════════════════
// MonitorView — Container con 3 tabs
// ═════════════════════════════════════════════════════════════════════════════

export default function MonitorView({ wsMessage }) {
  // Tab inicial: leer ?tab= del query string para deep-link desde la
  // galería forense (?tab=playback&site=…&camera_name=…&date=…&hour=…).
  const initialMode = (() => {
    const t = new URLSearchParams(window.location.search).get('tab')
    return t === 'playback' || t === 'forensic' ? t : 'live'
  })()
  const [mode, setMode] = useState(initialMode)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tab strip */}
      <div style={{
        display: 'flex', gap: 4, padding: '6px 10px',
        background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        {[
          { id: 'live',     label: tCem('Vivo','Live'),         icon: '●', color: '#10b981' },
          { id: 'playback', label: tCem('Reproducción','Playback'), icon: '◐', color: '#f59e0b' },
          { id: 'forensic', label: tCem('Forense','Forensic'),      icon: '◆', color: '#a855f7' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setMode(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 6,
              border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
              background: mode === t.id ? 'var(--accent)' : 'transparent',
              color: mode === t.id ? '#fff' : 'var(--text-secondary)',
            }}
          >
            <span style={{ color: mode === t.id ? '#fff' : t.color }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {mode === 'live'     && <LiveMonitor wsMessage={wsMessage} />}
        {mode === 'playback' && <PlaybackMonitor />}
        {mode === 'forensic' && <ForensicGallery />}
      </div>
    </div>
  )
}
